import React, { useMemo, useRef, useState, useEffect, Suspense } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Sky, Stars, OrbitControls, Environment, Lightformer, PointerLockControls } from '@react-three/drei';
import * as THREE from 'three';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { isTyping } from '../utils/typing';
import { SLICE_DISTRICT, IS_MOBILE, IS_IOS } from '../config';
import { emitMove } from '../services/worldSocket';
import { setCamPos, setCamBasis } from '../services/worldObjects';
import { floorBridge } from '../state/floorBridge';
import { gyroBridge } from '../state/gyroBridge';
import GyroLook from './GyroLook';
import StereoRender from './StereoRender';
import Peers from './Peers';
import PlacementLayer from './PlacementLayer';
import PhoneInHand from './PhoneInHand';
import { ROOMS, STREET_OVERVIEW, streetPoseFor } from '../rooms';
import { useWorldTime } from '../worldTime';
import CameraPanel, { CameraBridge } from './CameraPanel';
import BuildView from './BuildView';
import RideSequence from './RideSequence';
import HandoffWalkaway from './HandoffWalkaway';
import Spor1Lotus from './Spor1Lotus';
import WarmShaders from './WarmShaders';
import ShadowGovernor from './ShadowGovernor';
import AdaptiveResolution, { START_DPR } from './AdaptiveResolution';
import { introBridge } from '../state/introBridge';
import { STREET_SPAWN } from '../streetSpawn';

// Camera boundary — clamps the camera inside the current splat room's
// measured extents so visitors can't orbit out through a wall and see the
// room's fuzzy outer shell. Runs after OrbitControls' own update.
function CameraBounds() {
  const { camera } = useThree();
  const zoom = useWorldStore((s) => s.zoom);
  const roomBounds = useWorldStore((s) => s.roomBounds);
  const boundsEnabled = useWorldStore((s) => s.boundsEnabled);
  useFrame(() => {
    if (!boundsEnabled || zoom !== ZOOM.INTERIOR || !roomBounds) return;
    const p = camera.position;

    // A RING HALLWAY — the walkable part is the thick border of a disc.
    //
    // Two rectangles cannot say this. An outer box reaches into the corners
    // where the disc has no floor, and an inner box leaves four crescents open
    // between its sides and the curve. Two radii say it exactly, and the whole
    // test is one distance.
    const ring = roomBounds.ring;
    if (ring) {
      const cx = ring.cx || 0; const cz = ring.cz || 0;
      const dx = p.x - cx; const dz = p.z - cz;
      const d = Math.hypot(dx, dz) || 1e-6;
      // Pushed to the NEAR edge of whichever wall was crossed, so stepping a toe
      // over the inner rail nudges you back into the corridor rather than
      // catapulting you to the far side of the station.
      if (d > ring.outer) { p.x = cx + (dx / d) * ring.outer; p.z = cz + (dz / d) * ring.outer; }
      else if (d < ring.inner) { p.x = cx + (dx / d) * ring.inner; p.z = cz + (dz / d) * ring.inner; }
      if (roomBounds.min) p.y = Math.min(roomBounds.max[1], Math.max(roomBounds.min[1], p.y));
      return;
    }

    p.x = Math.min(roomBounds.max[0], Math.max(roomBounds.min[0], p.x));
    p.y = Math.min(roomBounds.max[1], Math.max(roomBounds.min[1], p.y));
    p.z = Math.min(roomBounds.max[2], Math.max(roomBounds.min[2], p.z));

    // A ROOM WHOSE MIDDLE IS NOT WALKABLE.
    //
    // A single box can only say "stay inside this". It cannot describe a
    // concourse where the walkable part is the corridor around the edge and the
    // middle is a track pit — and clamping into a box that includes the pit is
    // how you end up strolling across thin air over the rails.
    //
    // `hole` is the exclusion: outer box minus inner box. Being inside it is
    // resolved by pushing out along the axis you are LEAST far into, which is
    // the nearest wall and so the one you evidently just crossed. Pushing along
    // the deepest axis instead would teleport someone who stepped a foot over
    // the edge to the far side of the room.
    const h = roomBounds.hole;
    if (!h) return;
    if (p.x <= h.min[0] || p.x >= h.max[0] || p.z <= h.min[2] || p.z >= h.max[2]) return;
    const outLeft = p.x - h.min[0];
    const outRight = h.max[0] - p.x;
    const outBack = p.z - h.min[2];
    const outFront = h.max[2] - p.z;
    const least = Math.min(outLeft, outRight, outBack, outFront);
    if (least === outLeft) p.x = h.min[0];
    else if (least === outRight) p.x = h.max[0];
    else if (least === outBack) p.z = h.min[2];
    else p.z = h.max[2];
  });
  return null;
}
import Street from './Street';
import OsmCity from './OsmCity';
import CityModel from './CityModel';
import Bouncers from './Bouncers';
import VenueMarkers from './VenueMarkers';
import CityBillboards from './CityBillboards';
import WelcomeSign from './WelcomeSign';
import CitySkyline from './CitySkyline';
import CityBox from './CityBox';
import VenueFootprints from './VenueFootprints';
import VenueShells from './VenueShells';
import Portals from './Portals';
import GroundPits from './GroundPits';
import FloorClamp from './FloorClamp';
import SolidClamp from './SolidClamp';
import { boostFactor } from '../state/gymFuel';
import { BoostGlow } from './EnergyBoost';
import { sitedTurn, sitedLift, siteKey, townSited } from '../services/roomSpace';
import FacingOverlay, { FACING_OVERLAY } from './FacingOverlay';
import LotBuildings from './LotBuildings';
import SeatCamera from './SeatCamera';
import VenuePlacer from './VenuePlacer';
import BlockGrid from './BlockGrid';
import PlacedBuildings from './PlacedBuildings';
import StreetStream from './StreetStream';
import CityLife from './CityLife';
import AnimatedResident from './AnimatedResident';
import NpcManager from './NpcManager';
import CityResidents from './city/CityResidents';
import CityTalk from './city/CityTalk';
import CityStage from './city/CityStage';
import CitySounds from './city/CitySounds';
import CityCutscene from './city/CityCutscene';
import StoryRoomSwitch from './city/StoryRoomSwitch';
import { cutsceneBridge, useCutscene } from '../city/cutscene';
import { replayPending } from '../city/replay';
import { MOMENT_ID } from '../city/moment';
import MomentDirector from './city/MomentDirector';
import ChatSceneCamera from './ChatSceneCamera';
import ElevatorDirector from './hypeu/ElevatorDirector';
import Heliport from './heli/Heliport';
import HeliDirector from './heli/HeliDirector';
import { useHeliTour } from '../city/heliTour';
import { useSceneChat } from './SceneChat';
import { exitSpot } from '../city/cityPath';
import CityPlaces from './city/CityPlaces';
import VenueFacades from './VenueFacades';
import SiteSurvey from './SiteSurvey';
import { doorFront } from '../city/venueRegistry';
import BeatHud from './BeatHud';
import TouchControls from './TouchControls';
import { VRHide, VRSafe } from './VRStage';
import WasdControls from './WasdControls';
import { beatBridge } from '../state/beatBridge';
import { moveBridge } from '../state/moveBridge';

const IS_TOUCH = typeof window !== 'undefined' && (('ontouchstart' in window) || navigator.maxTouchPoints > 0
  || new URLSearchParams(window.location.search).get('touch') === '1');
import Interior from './Interior';
import { makeSoftClouds } from '../utils/cityFacade';
import SkyDome from './SkyDome';
import { NEXT_LOOK, QUALITY } from '../render/quality';
import SkyClock from '../render/SkyClock';
import ProceduralSky from '../render/ProceduralSky';
import WorldLight from '../render/WorldLight';
import EnvProbe from '../render/EnvProbe';
import PostFX from '../render/PostFX';
import { WalkBody, FEEL_CLASSIC } from '../game/walkBody';
import Vehicles from '../game/drive/Vehicles';
import { driveBridge, useDriveStore } from '../game/drive/driveStore';
import { player, usePovStore, TP_TEST_BODY } from '../game/player';
import { roomBody } from '../state/roomBody';
import { interiorFloor } from '../state/interiorFloor';
import { useHasBody } from '../hooks/usePov';
import { raycastWalls } from '../game/streetCollision';
import PlayerBody from '../game/PlayerBody';
import StreetLights from '../render/StreetLights';
import Rain from '../render/Rain';
import SkyTraffic from '../render/SkyTraffic';
import { streetAt } from '../render/roadMapBridge';
import { arrivalFromUrl } from '../city/cityArrivals';

// A high, slowly drifting cloud sheet — a first "sky variation" over the drei
// physical sky. Sits above the buildings, tinted by the time of day. (This is
// also the surface the distant-events video hack would later ride on.)
function CloudLayer({ tint = '#ffffff', opacity = 0.75 }) {
  const tex = useMemo(() => { const t = makeSoftClouds(); t.repeat.set(4, 4); return t; }, []);
  useEffect(() => () => tex.dispose(), [tex]);
  useFrame((_, dt) => { tex.offset.x += dt * 0.0035; tex.offset.y += dt * 0.0012; });
  return (
    <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 110, 0]} renderOrder={-1}>
      <planeGeometry args={[2200, 2200]} />
      <meshBasicMaterial map={tex} transparent opacity={opacity} depthWrite={false} color={tint} side={THREE.DoubleSide} toneMapped={false} />
    </mesh>
  );
}

// ?city=osm previews the real-Oakland OSM extrusion in place of the
// hand-built block (which stays the default so the demo never breaks).
const CITY = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('city') : null;
const OSM_MODE = CITY === 'osm';
const MODEL_MODE = CITY === 'model';
// The encircling ocean is GONE. Water is now a 'water' placement — positioned,
// oriented and sized with the tool that raycasts real ground — so the one pool
// in the block is the only water in the world. The skyline's terrain disc comes
// back with it, since it was only ever suppressed to let the sea show through.
// Walking eye height, in city-model units (the model is oversized, so this is
// not metres — residents stand 0.6–1.2 here).
//
// Was 0.65, set back when the cast had no trustworthy heights and everyone was
// rendering at the wrong scale anyway. Against properly sized residents that
// put the camera around chin height on the taller ones, which reads as short
// rather than as standing among them. 0.75 is the cast median — level with
// the average resident, with the four tallest still looking down at you. Tune
// live with ?eye=.
const EYE = (() => {
  const q = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get('eye'));
  return Number.isFinite(q) && q > 0 ? q : 0.75;
})();
// Where the orbit→street fly-in lands in the model city: eye level, well
// INSIDE the city (not at its edge), looking across it. Walking takes over
// from exactly here — no jump, and no long walk to reach the buildings.
// Recorded spot (2026-07-23, ?cam=edit "copy spawn"): stands at z≈25.5 looking
// −z across the block. pos.y stays tied to EYE so eye-height edits carry.
const MODEL_WALK_START = { pos: [STREET_SPAWN.pos[0], EYE, STREET_SPAWN.pos[1]], look: STREET_SPAWN.look };
// Walking used to be clamped to a RADIUS, which is the wrong shape for a
// rectangular city: a circle wide enough to reach the long ends lets you walk
// well past the short sides. Now it clamps to the model's own footprint, inset
// so you stop short of the enclosing walls rather than pressing your face to one.
// Falls back to the old radius until CityModel has reported its bounds.
const WALK_BOUND = 160;
const WALK_INSET = 6;
// THE OUTER LANES (2026-11-03, Khari: "the street in front of gangstafuel gym is
// blocked. only one lane is walkable. the other lane sits against the border of
// the city … the boundary may be a bit too tight, cutting into the outer lane").
// A street that runs along the city's edge (the gym's door faces one, 8 units
// from it) lost everything past the 6-unit inset — its far lane and kerb. Now the
// inset band is walkable where the street map (render/roadMap, roadMapBridge
// streetAt) says it is road or pavement, out to WALK_EDGE from the model's edge;
// anything else in the band (a wall, the empty ground past the last kerb) still
// stops you. Before the map exists, the old inset holds.
const WALK_EDGE = 0.8;
// Third-person chase, in BODY HEIGHTS (the cast runs 0.6–1.2 units tall): how
// far behind the camera stands, and how high up the body the point it frames.
const THIRD_DIST = 2.7;
const THIRD_HEAD = 1.05;
// ?freecam=1 — take both leashes off: the eye-height lock AND the map boundary.
// Q/E fly down/up. For inspecting what the world is actually doing, including
// underneath it, which is the one place a clamped walker can never look.
const FREECAM = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('freecam') === '1';
// Same shape as EYE's ?eye= knob: a positive number, or nothing.
const qpNum = (k) => {
  const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get(k));
  return Number.isFinite(v) && v > 0 ? v : null;
};

// One-shot: the Enter cinematic must fire exactly ONCE (on the real Enter), not
// every time CameraRig re-mounts (it unmounts/remounts crossing street↔district).
// Module-level so it survives remounts; resets on page reload.
let introFired = false;

// Camera rig — flies between zoom levels, then hands control to the user
// (OrbitControls). The descent IS the LOD strategy; once you arrive you can
// drag to look around and scroll to zoom.
function CameraRig({ controls }) {
  const { camera } = useThree();
  const zoom = useWorldStore((s) => s.zoom);
  const interior = useWorldStore((s) => s.interior);
  const spawnOverride = useWorldStore((s) => s.spawnOverride);
  const streetFocus = useWorldStore((s) => s.streetFocus);
  const streetArrival = useWorldStore((s) => s.streetArrival);
  const streetDoors = useWorldStore((s) => s.streetDoors);
  /* ?at=<place> — a link that arrives at a place (city/cityArrivals; first
     ?at=plaza, from the stage notice). Set once, as the street arrival, so the
     orbit→street fly-in lands there and walking starts from it. */
  useEffect(() => {
    const arrival = arrivalFromUrl(EYE);
    if (arrival) {
      useWorldStore.getState().setStreetArrival(arrival);
      console.log(`[rig] ?at= arrival → [${arrival.pos.map((n) => Number(n).toFixed(1)).join(', ')}]`);
    }
  }, []);
  const roomConfigs = useWorldStore((s) => s.roomConfigs);
  const venueAnchors = useWorldStore((s) => s.venueAnchors);
  const venueBoxes = useWorldStore((s) => s.venueBoxes);
  const target = useMemo(() => {
    // Interior rooms are metric (splat fit grounds the floor at y=0 and
    // centers the room on the origin) — spawn is per-room (designed spawn,
    // Demo DoD #2), with a manifest-shipped override winning when present.
    // SAVED SPAWN BEATS THE AUTHORED ONE, and is read straight from the store
    // rather than waiting to be pushed in as a spawnOverride. The override
    // arrives when a room's config finishes loading, which is after the camera
    // has already flown to the authored spawn and settled — so the measured
    // spawn was being computed correctly and then ignored. RoomConfigs loads
    // every room's row at start-up so this is answerable synchronously.
    // A registry instance with no row of its own reads its base room's, as
    // MeshInterior does — the same object, so spawn and fit cannot disagree.
    const savedSpawn = (roomConfigs?.[interior] ?? (ROOMS[interior]?.venueBase ? roomConfigs?.[ROOMS[interior].venueBase] : undefined))?.spawn;
    let spawn = spawnOverride || savedSpawn
      || ROOMS[interior]?.spawn || { pos: [0, 1.7, 3.2], look: [0, 1.3, -1.2] };
    // WHICH SPAWN WON, and what it is. "I still spawn in the centre" has three
    // causes that are indistinguishable from the floor: no row was saved, a row
    // exists and something outranks it, or the row is being used and the ROOM
    // moved out from under it — growing a room scales it about its own centre
    // while the spawn stays where it is, so a point measured in a corridor ends
    // up nearer the middle the bigger the room gets.
    if (interior) {
      const src = spawnOverride ? 'splat/override' : savedSpawn ? 'SAVED row' : 'rooms.js default';
      console.log(`[venue] ${interior} spawn from ${src}: [${spawn.pos.map((n) => Number(n).toFixed(2)).join(', ')}]`
        + (savedSpawn && !spawnOverride ? '' : savedSpawn ? ' — a saved row exists but is being outranked' : ' — nothing saved for this room'));
    }

    // HOW TALL YOU ARE INDOORS.
    //
    // These rooms were authored around a 1.6 eye, which is a person in metres —
    // but THIS WORLD IS NOT IN METRES. The street eye is 0.75 (see EYE above:
    // tuned against the residents, who are the reference for human scale here),
    // so walking into a venue has been making you 2.1x taller than you were on
    // the pavement a second earlier. That is why a shop tuned to look right ends
    // up feeling like it was built for a child: the room was sized against the
    // wrong body.
    //
    // ?roomeye= tunes it live; the answer BAKES INTO spawn.pos[1], which is and
    // always was this room's eye height. Deliberately not a separate rooms.js
    // `eye` field: the camera panel's "copy spawn" writes pos[1], so a second
    // source of truth would silently outrank the tool built for this job.
    //
    // A separate param from ?eye= on purpose — that one is the STREET's, and
    // changing one should not silently move the other.
    const eyeQ = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get('roomeye'));
    const eye = Number.isFinite(eyeQ) && eyeQ > 0 ? eyeQ : null;
    if (eye) {
      // Keep the authored PITCH: the look point moves with the eye, so a spawn
      // framed looking slightly down still looks slightly down from the new
      // height rather than snapping level.
      const dy = spawn.look[1] - spawn.pos[1];
      spawn = {
        pos: [spawn.pos[0], eye, spawn.pos[2]],
        look: [spawn.look[0], eye + dy, spawn.look[2]],
      };
    }
    // A SITED room stands at its building, not at the origin, so its spawn — which
    // is authored in room-local space like every other room's — has to be carried
    // out there with it. Without this the camera lands at 0,0,0 and the venue is
    // somewhere across town.
    const siteAt = ROOMS[interior]?.placement === 'sited' ? venueAnchors?.[siteKey(interior)] : null;
    if (siteAt) {
      // A venue the town built stands TURNED with its site (services/roomSpace
      // sitedTurn: HQ faces back toward the city), so its room-local spawn turns
      // with it before it moves out there (2026-10-31). The built-in sited rooms
      // keep the shift they were tuned against (their saved spawns assume it).
      const th = townSited(ROOMS[interior]) ? sitedTurn(interior) : 0;
      const lift = sitedLift(interior);   // …and stands on the street's ground
      const c = Math.cos(th); const s = Math.sin(th);
      const out = (p) => [p[0] * c + p[2] * s + siteAt[0], p[1] + lift, -p[0] * s + p[2] * c + siteAt[2]];
      spawn = { pos: out(spawn.pos), look: out(spawn.look) };
    }
    // Street: frame the focused venue (picked on the HUD map) or the block
    // overview. The descent approaches from −z (the road side): facades, the
    // sun and the skyline all face that way — arriving from +z showed the
    // storefronts' blank backs.
    // The model city hands off to walking, so its fly-in lands at eye level —
    // beside the parked car once the model reports its position, else a
    // central fallback.
    // Exiting a venue returns to its building; first entry lands by the car.
    const anchor = MODEL_MODE && streetFocus && venueAnchors ? venueAnchors[streetFocus] : null;
    // Out on the street the street reaches, facing the building you left: the
    // nearest standing ground to it that connects to the spawn (city/cityPath
    // exitSpot). A fixed (+6, +6) from the building's centre put you inside the
    // block next to the concert hall, sealed in (2026-10-23). The old offset is
    // only the fallback for before the road map exists.
    const box = streetFocus && venueBoxes ? venueBoxes[streetFocus] : null;
    const reachOut = box?.size ? Math.hypot(box.size[0] || 0, box.size[2] || 0) / 2 + 8 : 16;
    // A venue the town voted for has a known DOOR (its site's), so you come out
    // of it, on its street, looking back at it — rather than at the nearest
    // reachable pavement to the middle of the building, which can be round the
    // back. Searched from just outside the door, so a tight radius finds it.
    const reg = anchor ? useWorldStore.getState().venueRegistry?.[streetFocus] : null;
    const front = reg ? doorFront(reg.site, useWorldStore.getState().venueAnchorOverrides?.[streetFocus], 2.2) : null;
    const out = anchor
      ? (front ? exitSpot(front, [STREET_SPAWN.pos[0], STREET_SPAWN.pos[1]], { radius: 6 }) : null)
        || exitSpot([anchor[0], anchor[2]], [STREET_SPAWN.pos[0], STREET_SPAWN.pos[1]], { radius: Math.max(16, reachOut) })
      : null;
    const lookAt = front ? doorFront(reg.site, useWorldStore.getState().venueAnchorOverrides?.[streetFocus], 0) : [anchor?.[0], anchor?.[2]];
    const anchorPose = !anchor ? null
      : out ? { pos: [out[0], EYE, out[1]], look: [lookAt[0], EYE, lookAt[1]] }
        : { pos: [anchor[0] + 6, EYE, anchor[2] + 6], look: [anchor[0], EYE, anchor[2]] };
    // First street entry lands at the RECORDED spawn (MODEL_WALK_START). The old
    // carPose (land beside the parked car, from `landmark`) was overriding it —
    // dropped so the chosen spawn wins. Venue-return still uses anchorPose.
    // An explicit arrival wins over the venue's own storefront. Set only when
    // you surface somewhere you did not go in — the transit case — so every
    // ordinary venue still returns you to its door.
    // A ROOM WITH A STREET DOOR PLACED BY HAND lets you out in front of THAT door
    // (2026-10-31: The Underground is an entrance now, not a building; the Corner
    // Market and the Warehouse are not street-facing) — Portals publishes where.
    const handDoor = streetFocus ? streetDoors?.[streetFocus] || null : null;
    const street = streetArrival
      || (MODEL_MODE ? (handDoor || anchorPose || MODEL_WALK_START) : (streetPoseFor(streetFocus) || STREET_OVERVIEW));
    return {
      [ZOOM.ORBIT]: { pos: [0, 140, -160], look: [0, 0, 0] },
      [ZOOM.DISTRICT]: { pos: [0, 42, -64], look: [0, 0, 6] },
      [ZOOM.STREET]: street,
      [ZOOM.INTERIOR]: spawn
    };
  }, [interior, spawnOverride, streetFocus, streetArrival, roomConfigs, venueAnchors, venueBoxes, streetDoors]);

  const anim = useRef({ active: true, t: 0, toQuat: null });
  const setCamSettled = useWorldStore((s) => s.setCamSettled);
  // The room you were last in, so leaving a SITED one can land you outside at once (below).
  const lastRoom = useRef(null);
  useEffect(() => { if (interior) lastRoom.current = interior; }, [interior]);

  // Restart the fly-to animation whenever the zoom level or spawn changes.
  // The first descent into the city (Enter → STREET, no venue focus yet) plays
  // as a CINEMATIC drone move — a banking orbit around the city that drops and
  // zooms into the landing. Quick-travel / venue arrivals use the direct ease.
  useEffect(() => {
    const cinematic = MODEL_MODE && zoom === ZOOM.STREET && !streetFocus && !interior;
    // OUT OF A SITED ROOM YOU ARE SIMPLY OUTSIDE (2026-10-31, Khari: "when I exit,
    // I am spawned back into the building … the front area of the building is
    // sensitive and throws me back in"). A sited room stands IN its building, so
    // the ease from where you stood to the pavement ran through its walls — and
    // through its own door's trigger, which opened the room again. You walked out
    // of a door: you are on the pavement, the way a first-person room is entered.
    const fromSited = zoom === ZOOM.STREET && !interior && !!lastRoom.current && ROOMS[lastRoom.current]?.placement === 'sited';
    lastRoom.current = null;
    anim.current = { active: true, t: 0, toQuat: null, cinematic, from: camera.position.clone(), snap: fromSited };
    setCamSettled(false);
    // ARMING IS THE THING TO WATCH, not the flight.
    //
    // While this is active it lerps the camera's POSITION and slerps its
    // ROTATION toward the spawn every frame — so you walk and turn against a
    // spring, smoothly, with no other symptom. If it re-arms (this effect keys
    // on `target`, a memo over six values), it does that forever and the room
    // simply feels heavy.
    //
    // One line per arm makes a repeat obvious: a stream of these while you are
    // standing still IS the bug, and nothing else in the world produces it.
    console.log(`[rig] fly-in armed → ${interior || 'street'}`
      + (interior && ROOMS[interior]?.firstPerson ? ' (first-person: lands immediately)' : ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, target, setCamSettled]);

  // Reliable ENTRY trigger. The zoom-change effect above only fires the fly-in on
  // a zoom CHANGE, and the loader's Enter (ORBIT→STREET) wasn't registering as one
  // — so the camera sat at the low spawn ("underground") until manual district↔
  // street nav re-triggered it. When `entered` flips, force a fresh cinematic
  // drone descent from the overhead, at STREET.
  const entered = useWorldStore((s) => s.entered);
  const setZoom = useWorldStore((s) => s.setZoom);
  // …AND NOT WHILE THEY ARE STILL CHOOSING (Khari, 2026-08-04: "im pretty sure it
  // is still being triggered while I am in the selection screen. the login/
  // entrance is triggering it"). Right: the Loader's own Enter flips `entered`
  // to dismiss the loading screen, and this effect took that as the cue to fly
  // in — so the descent ran, with its music, behind the picker. `entered` cannot
  // simply be withheld, because the Loader uses it to know when to get out of
  // the way. So the flag keeps meaning "loading is done" and the CINEMATIC waits
  // on the choice as well. `introFired` stays false until it actually runs, so
  // pressing ENTER on the city re-runs this and the descent plays then.
  const charSelect = useWorldStore((s) => s.charSelect);
  useEffect(() => {
    if (!entered || charSelect) return;
    // NO CINEMATIC WILL RUN THIS MOUNT — it already played (a remount / hot
    // reload; `introFired` is module scope) or model mode is off. There is no
    // landing to wait for, so arrival is NOW.
    //
    // This is a safety net on the story's most important beat: `arrivedAt`
    // gates Lyra's call, so a path that never stamps it is a path where the
    // phone never rings at all. Fail toward the call happening.
    if (!MODEL_MODE || introFired) { useWorldStore.getState().markArrived(); return; }
    if (useWorldStore.getState().interior) return; // don't hijack an interior entry
    introFired = true;
    camera.position.set(0, 140, -160);
    anim.current = { active: true, t: 0, toQuat: null, cinematic: true, from: camera.position.clone() };
    introBridge.active = true; introBridge.t = 0; introBridge.skip = false;
    setCamSettled(false);
    setZoom(ZOOM.STREET);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entered, charSelect]);

  useFrame((_, delta) => {
    const a = anim.current;
    if (!a.active) return;
    const tgt = target[zoom] || target[ZOOM.ORBIT];

    // Controls stay OFF during flight — aiming at a mid-lerp target flies the
    // camera in backwards when the destination view faces the approach path.
    // Instead the camera's orientation slerps toward the final framing while
    // the position flies, then controls take over exactly on the spawn.
    if (controls?.current) controls.current.enabled = false;

    // ── Cinematic drone descent ──────────────────────────────────────────
    // A banking orbit around the city center that drops altitude and pulls in
    // its radius (zoom), looking at the city the whole way, then eases onto the
    // exact landing pose + heading so WalkControls can adopt it cleanly.
    if (a.cinematic) {
      // Cinematic drone descent — a banking orbit that drops in over the city and
      // eases onto the landing (the original entry; restored). introBridge drives
      // the titling overlay + Skip button. Skip → jump to the end.
      const DUR = 6.0;
      a.t += delta;
      if (introBridge.skip) a.t = DUR;
      const s = Math.min(1, a.t / DUR);
      introBridge.active = true;
      introBridge.t = s;
      const e = s * s * (3 - 2 * s);                 // smoothstep along the path
      // Always aim at the STREET landing directly (not target[zoom]) — the Enter
      // trigger can arm this a frame before `zoom` propagates to STREET, and
      // target[ORBIT] would send the drone circling high over the dark city center
      // (a black frame) before snapping down. This keeps it descending to the spawn.
      const stgt = target[ZOOM.STREET] || tgt;
      const P1 = stgt.pos;
      const cx = stgt.look[0], cz = stgt.look[2];    // circle the landing area
      const dx = a.from.x - cx, dz = a.from.z - cz;
      const angStart = Math.atan2(dz, dx);
      const radStart = Math.hypot(dx, dz) || 1;
      const radEnd = Math.hypot(P1[0] - cx, P1[2] - cz) || 1;
      const ang = angStart + Math.PI * 1.35 * e;      // ~245° banking sweep
      const rad = THREE.MathUtils.lerp(radStart, Math.max(radEnd, 9), e);
      const h = THREE.MathUtils.lerp(a.from.y, P1[1], e);
      const sx = cx + Math.cos(ang) * rad, sz = cz + Math.sin(ang) * rad;
      // Last 28% eases the spiral onto the precise landing pose.
      const w = Math.max(0, Math.min(1, (s - 0.72) / 0.28));
      const ww = w * w * (3 - 2 * w);
      camera.position.set(
        THREE.MathUtils.lerp(sx, P1[0], ww),
        THREE.MathUtils.lerp(h, P1[1], ww),
        THREE.MathUtils.lerp(sz, P1[2], ww),
      );
      // Frame the city center (raised) while circling, easing to the landing look.
      const lookAt = new THREE.Vector3(cx, 6, cz).lerp(new THREE.Vector3(...tgt.look), ww);
      camera.quaternion.setFromRotationMatrix(
        new THREE.Matrix4().lookAt(camera.position, lookAt, new THREE.Vector3(0, 1, 0))
      );
      if (s >= 1) {
        camera.position.set(P1[0], P1[1], P1[2]);
        camera.quaternion.setFromRotationMatrix(
          new THREE.Matrix4().lookAt(new THREE.Vector3(...P1), new THREE.Vector3(...tgt.look), new THREE.Vector3(0, 1, 0))
        );
        a.active = false;
        introBridge.active = false;
        introBridge.skip = false;
        setCamSettled(true);
        // A REPLAY starts out of the descent (city/replay): it landed on the
        // first scene's opening frame, so hold the camera there for the scene —
        // otherwise the walker takes it for the frames until the scene does.
        if (replayPending()) cutsceneBridge.active = true;
        // FEET DOWN. This is the moment the story's clock starts — see
        // worldStore.arrivedAt for why camSettled could never mark it.
        useWorldStore.getState().markArrived();
      }
      return;
    }

    if (!a.toQuat) {
      const m = new THREE.Matrix4().lookAt(
        new THREE.Vector3(...tgt.pos),
        new THREE.Vector3(...tgt.look),
        new THREE.Vector3(0, 1, 0)
      );
      a.toQuat = new THREE.Quaternion().setFromRotationMatrix(m);
    }

    // ── A WALK-IN ROOM IS NOT FLOWN INTO ─────────────────────────────────────
    //
    // The ease below starts from wherever the camera WAS — out on the street,
    // outside the building — and drifts to the spawn over up to 3.2 seconds. In
    // an orbit room that reads as a camera move. In a FIRST-PERSON room it reads
    // as being dragged through the wall, because you are the body: you went
    // through a door and should simply be inside.
    //
    // It is also actively harmful in a mesh room. The drift path crosses real
    // geometry, and FloorClamp is probing the floor at every point along it —
    // so the camera gets yanked onto the train roof, then the platform, then
    // nothing, while the rig hauls it toward the spawn. That fight is the
    // jitter, and it is worst where the path passes closest to something solid.
    //
    // Orbit rooms keep the ease: there is no body there and the arrival is a
    // shot, not a step.
    if ((interior && ROOMS[interior]?.firstPerson) || a.snap) {
      camera.position.set(tgt.pos[0], tgt.pos[1], tgt.pos[2]);
      camera.quaternion.copy(a.toQuat);
      a.active = false;
      setCamSettled(true);
      return;
    }

    a.t += delta;
    const k = 1 - Math.exp(-2.4 * delta);
    camera.position.x += (tgt.pos[0] - camera.position.x) * k;
    camera.position.y += (tgt.pos[1] - camera.position.y) * k;
    camera.position.z += (tgt.pos[2] - camera.position.z) * k;
    camera.quaternion.slerp(a.toQuat, k);

    const dp = Math.hypot(tgt.pos[0] - camera.position.x, tgt.pos[1] - camera.position.y, tgt.pos[2] - camera.position.z);
    if (dp < 0.06 || a.t > 3.2) {
      // Land EXACTLY: any residual error can exceed a close-pivot framing
      // (0.32m) and flip the view. Then hand a clean state to the controls.
      camera.position.set(tgt.pos[0], tgt.pos[1], tgt.pos[2]);
      camera.quaternion.copy(a.toQuat);
      if (controls?.current) {
        controls.current.target.set(tgt.look[0], tgt.look[1], tgt.look[2]);
        controls.current.enabled = true;
        controls.current.update();
      }
      a.active = false;
      setCamSettled(true);
      // No cinematic this time (a remount, a re-entry, model mode off) — the
      // street settling IS the arrival. Guarded so the pre-intro orbit ease,
      // which runs while CharacterSelect is still up, can never claim it.
      if (!introBridge.active && zoom === ZOOM.STREET && !useWorldStore.getState().charSelect) {
        useWorldStore.getState().markArrived();
      }
    }
  });
  return null;
}

// Map-nav flythrough: once the street camera has flown to and settled on the
// venue we're heading into, dwell a beat (so the arrival reads) then enter.
function AutoEnter() {
  const camSettled = useWorldStore((s) => s.camSettled);
  const zoom = useWorldStore((s) => s.zoom);
  const streetFocus = useWorldStore((s) => s.streetFocus);
  const pendingEnter = useWorldStore((s) => s.pendingEnter);
  const enterInterior = useWorldStore((s) => s.enterInterior);
  useEffect(() => {
    if (pendingEnter && camSettled && zoom === ZOOM.STREET && streetFocus === pendingEnter) {
      const t = setTimeout(() => enterInterior(pendingEnter), 700);
      return () => clearTimeout(t);
    }
  }, [pendingEnter, camSettled, zoom, streetFocus, enterInterior]);
  return null;
}

// WalkControls — first-person street-level exploration with DRAG-to-look (no
// pointer lock, so the cursor stays free and clicking the HUD map/quick-travel
// never breaks movement). Drag on the scene to look, WASD/arrows to walk,
// Shift to run; the camera stays pinned at eye height. No collision yet.
function WalkControls({ eye = 1.7, speed = 9, sensitivity = 0.0026, drone = false }) {
  // `speed` is now the drone / ?feel=classic pace; walking runs at walkBody's.
  const { camera, gl } = useThree();
  const keys = useRef({});
  const drag = useRef({ on: false, x: 0, y: 0 });
  const look = useRef(new THREE.Euler(0, 0, 0, 'YXZ'));
  const travel = useRef(null);
  // True for the frames RideSequence owned the camera — on hand-back the look
  // euler re-adopts the heading the car left, or the first drag would snap the
  // view back to wherever you faced before getting in.
  const rode = useRef(false);
  const walkTarget = useWorldStore((s) => s.walkTarget);
  const setWalkTarget = useWorldStore((s) => s.setWalkTarget);
  const enterInterior = useWorldStore((s) => s.enterInterior);
  // Reused for the per-frame position/heading publish below.
  const fwdPub = useRef(new THREE.Vector3());
  // The body under the camera: momentum, walls, gait, sprint, jump (game/walkBody).
  const body = useRef(null);
  if (!body.current) body.current = new WalkBody(camera);
  const wish = useRef(new THREE.Vector3());
  // THIRD PERSON (game/player): only for a player with a body to look at — the
  // world's earn-your-body rule. V toggles; the choice is remembered.
  const hasOwnBody = useHasBody();
  const hasBody = hasOwnBody || TP_TEST_BODY != null;
  const wantThird = usePovStore((s) => s.wantThird);
  const thirdOn = useRef(false);
  const pvec = useRef(new THREE.Vector3());
  const chase = useRef({ pos: new THREE.Vector3(), init: false, fwd: new THREE.Vector3(), side: new THREE.Vector3(), head: new THREE.Vector3(), want: new THREE.Vector3(), dir: new THREE.Vector3() });

  // Quick travel: a smooth flyover from where you stand to the venue (rising up
  // and settling in front of it), then step inside. Not a teleport.
  useEffect(() => {
    if (!walkTarget) return;
    const toPos = new THREE.Vector3(walkTarget.pos[0], eye, walkTarget.pos[2]);
    const m = new THREE.Matrix4().lookAt(toPos, new THREE.Vector3(walkTarget.look[0], eye, walkTarget.look[2]), new THREE.Vector3(0, 1, 0));
    travel.current = {
      fromPos: camera.position.clone(),
      toPos,
      fromQuat: camera.quaternion.clone(),
      toQuat: new THREE.Quaternion().setFromRotationMatrix(m),
      t: 0, dur: 1.7,
      enter: walkTarget.enter || null,
    };
    setWalkTarget(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walkTarget]);

  useEffect(() => {
    // Adopt wherever the fly-in landed (don't snap) — pin to eye height + heading.
    // NOT in drone mode: the building tool is entered from above, and snapping to
    // eye height would drop you to the pavement the instant you opened it — the
    // opposite of what a plan-view editor is for.
    if (!drone) camera.position.y = eye;
    look.current.setFromQuaternion(camera.quaternion);
    look.current.z = 0;
    const dom = gl.domElement;
    // Look-drag tracks ONE pointer id — the id filter on move means the joystick
    // finger (captured, but its moves still bubble to window) never hijacks the
    // view, so move + look run at once. pointercancel is handled too: Android
    // fires it (NOT pointerup) for the look finger during multi-touch, and without
    // it drag.on would stick true and kill every later look-drag.
    const onDown = (e) => { drag.current = { on: true, x: e.clientX, y: e.clientY, id: e.pointerId }; };
    const onMove = (e) => {
      if (!drag.current.on || drag.current.id !== e.pointerId || travel.current) return;
      if (useWorldStore.getState().ride || driveBridge.active) return;   // the car has the camera
      // …and so does a scene (a heli tour, a street cutscene): turning the
      // camera here fought it every frame — the flicker and stutter when you
      // dragged during one (Khari, 2026-11-01). A scene that lets you look round
      // does it itself (HeliDirector's window seat). (The rooms' handler below
      // already stands down: a scene clears `adopted`.)
      if (cutsceneBridge.active) return;
      const dx = e.clientX - drag.current.x, dy = e.clientY - drag.current.y;
      drag.current.x = e.clientX; drag.current.y = e.clientY;
      const L = look.current;
      L.y -= dx * sensitivity;
      L.x = Math.max(-Math.PI / 2 + 0.05, Math.min(Math.PI / 2 - 0.05, L.x - dy * sensitivity));
      if (!gyroBridge.active) camera.quaternion.setFromEuler(L);
    };
    const onUp = (e) => { if (drag.current.id === e.pointerId) drag.current.on = false; };
    // A keystroke that landed in a text field belongs to that field, not to the
    // world. `up` is NOT guarded: releasing a key must always clear it, or a
    // held W at the moment focus moves into a chat box walks you forever.
    const down = (e) => {
      if (isTyping(e)) return;
      keys.current[e.code] = true;
      // SPACE JUMPS — and must not also "click" whatever button kept focus
      // (Enter, a map marker), which is what Space does to a focused button.
      if (e.code === 'Space' && !drone && !FEEL_CLASSIC && !driveBridge.active) {
        e.preventDefault();
        if (document.activeElement && document.activeElement !== document.body && document.activeElement.tagName === 'BUTTON') document.activeElement.blur();
        if (!e.repeat) body.current?.jump();
      }
    };
    const up = (e) => { keys.current[e.code] = false; if (e.code === 'Space' && !isTyping(e)) e.preventDefault(); };
    dom.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      dom.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera, gl]);
  useFrame((_, dt) => {
    // Spor1's ride: RideSequence owns the camera outright — nothing here may
    // move it, pin it to eye height, or emit for it while the car is driving.
    // A car owns the camera the same way (game/drive) — stand down, and adopt
    // wherever it leaves you when you step out.
    // A CUTSCENE the same way (city/cutscene): it saves your view and puts it
    // back exactly, so the hand-back below adopts where you were standing.
    if (useWorldStore.getState().ride || driveBridge.active || cutsceneBridge.active) {
      if (!rode.current) body.current?.release(camera);
      rode.current = true; return;
    }
    if (rode.current) {
      rode.current = false;
      look.current.setFromQuaternion(camera.quaternion);
      look.current.z = 0;
      pvec.current.set(camera.position.x, 0, camera.position.z); chase.current.init = false;
    }
    // Quick-travel flyover: rise up, glide to the venue, settle facing it,
    // then step inside. WASD/drag are suspended while it plays.
    const tr = travel.current;
    if (tr) {
      tr.t += dt;
      const k = Math.min(1, tr.t / tr.dur);
      const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2; // easeInOut
      camera.position.lerpVectors(tr.fromPos, tr.toPos, e);
      camera.position.y = THREE.MathUtils.lerp(tr.fromPos.y, tr.toPos.y, e) + Math.sin(e * Math.PI) * 10; // arc up
      camera.quaternion.slerpQuaternions(tr.fromQuat, tr.toQuat, e);
      if (k >= 1) {
        camera.position.copy(tr.toPos);
        camera.quaternion.copy(tr.toQuat);
        look.current.setFromQuaternion(camera.quaternion); look.current.z = 0;
        pvec.current.set(tr.toPos.x, 0, tr.toPos.z); chase.current.init = false;
        const enter = tr.enter;
        travel.current = null;
        if (enter) enterInterior(enter);
      }
      // Broadcast during the flyover too, so peers watch you travel (they floor-
      // anchor, so the arc-up Y is ignored on their side).
      emitMove([camera.position.x, camera.position.y, camera.position.z], camera.rotation.y);
      return;
    }
    const k = keys.current;
    // Arrow keys ROTATE the view (look without the trackpad); WASD still moves.
    {
      const L = look.current;
      const rot = 1.6 * dt; // radians/sec
      let looked = false;
      if (k.ArrowLeft) { L.y += rot; looked = true; }
      if (k.ArrowRight) { L.y -= rot; looked = true; }
      if (k.ArrowUp) { L.x = Math.min(Math.PI / 2 - 0.05, L.x + rot); looked = true; }
      if (k.ArrowDown) { L.x = Math.max(-Math.PI / 2 + 0.05, L.x - rot); looked = true; }
      if (looked && !gyroBridge.active) camera.quaternion.setFromEuler(L);
    }
    const fwd = new THREE.Vector3();
    camera.getWorldDirection(fwd); fwd.y = 0;
    if (fwd.lengthSq() < 1e-6) return;
    fwd.normalize();
    const right = new THREE.Vector3().crossVectors(fwd, new THREE.Vector3(0, 1, 0)).normalize();
    const move = wish.current.set(0, 0, 0);
    if (k.KeyW) move.add(fwd);
    if (k.KeyS) move.sub(fwd);
    if (k.KeyD) move.add(right);
    if (k.KeyA) move.sub(right);
    move.addScaledVector(fwd, moveBridge.y);      // on-screen joystick (touch)
    move.addScaledVector(right, moveBridge.x);
    if (move.length() > 1) move.normalize();      // clamp (keeps analog joystick partials)
    const sprinting = !!(k.ShiftLeft || k.ShiftRight);
    // Hoisted: the freecam branch below needs it too, and a const scoped inside
    // the move block would throw there the moment you pressed Q or E.
    const run = sprinting ? 2.2 : 1;
    if (FREECAM || drone || FEEL_CLASSIC) {
      // The old instant, unobstructed movement — kept for the drone and the
      // building tool (which fly), and as the ?feel=classic A/B.
      if (move.lengthSq() > 0) camera.position.addScaledVector(move, speed * run * dt);
    }
    if (FREECAM || drone) {
      // Q down, E up — vertical has to be its own input, because WASD is
      // strictly horizontal and simply unlocking y would leave you at eye
      // height with no way to descend.
      const vy = (k.KeyE ? 1 : 0) - (k.KeyQ ? 1 : 0);
      if (vy) camera.position.y += vy * speed * run * dt * 2;
    } else {
      // A BODY, NOT A DRONE: momentum, walls, a gait, a jump (game/walkBody).
      // In third person the BODY is what moves (pvec) and the camera follows it;
      // in first person the camera is the body, as it always was.
      const third = wantThird && hasBody && !FEEL_CLASSIC && !gyroBridge.active && NEXT_LOOK;
      if (third !== thirdOn.current) {
        if (third) { pvec.current.set(camera.position.x, 0, camera.position.z); chase.current.init = false; }
        else camera.position.set(pvec.current.x, eye, pvec.current.z);
        thirdOn.current = third;
        player.third = third;
      }
      const pos = third ? pvec.current : camera.position;
      const px0 = pos.x; const pz0 = pos.z;   // where this frame's step started (the boundary may hand it back)
      let lift = 0; let roll = 0;
      if (!FEEL_CLASSIC) {
        const b = body.current.step(dt, move, sprinting, camera, { pos, gait: !third });
        lift = b.lift; roll = b.roll;
      }
      // Boundary: keep inside the playable area so you never reach the void /
      // the skyline ring — the city stays "endless".
      const r = Math.hypot(pos.x, pos.z);
      const cb = useWorldStore.getState().cityBounds;
      if (cb) {
        const inner = (x, z) => x >= cb.min[0] + WALK_INSET && x <= cb.max[0] - WALK_INSET && z >= cb.min[1] + WALK_INSET && z <= cb.max[1] - WALK_INSET;
        const mapped = streetAt(pos.x, pos.z) !== null;
        const pad = mapped ? WALK_EDGE : WALK_INSET;
        pos.x = Math.min(cb.max[0] - pad, Math.max(cb.min[0] + pad, pos.x));
        pos.z = Math.min(cb.max[1] - pad, Math.max(cb.min[1] + pad, pos.z));
        // In the outer band: street only. Off it, keep whichever half of the step
        // stays on street (sliding along the kerb), else stay where you were.
        if (mapped && !inner(pos.x, pos.z) && !streetAt(pos.x, pos.z)) {
          const ok = (x, z) => inner(x, z) || streetAt(x, z);
          if (ok(pos.x, pz0)) pos.z = pz0;
          else if (ok(px0, pos.z)) pos.x = px0;
          else { pos.x = px0; pos.z = pz0; }
        }
      } else if (r > WALK_BOUND) {
        const kk = WALK_BOUND / r; pos.x *= kk; pos.z *= kk;
      }
      player.x = pos.x; player.z = pos.z; player.y = 0; player.jump = lift;
      player.vx = body.current.vel.x; player.vz = body.current.vel.z;
      if (third) {
        // Over the shoulder: aim where you look, stand behind and a little to
        // the right of the head, pull in when a wall is in the way.
        const c = chase.current;
        camera.quaternion.setFromEuler(look.current);
        camera.getWorldDirection(c.fwd);
        c.side.set(-c.fwd.z, 0, c.fwd.x).normalize();
        const bh = player.height || 0.85;
        c.head.set(pos.x, bh * THIRD_HEAD + lift, pos.z).addScaledVector(c.side, bh * 0.38);
        c.want.copy(c.head).addScaledVector(c.fwd, -bh * THIRD_DIST);
        if (c.want.y < 0.3) c.want.y = 0.3;
        c.dir.copy(c.want).sub(c.head);
        const len = c.dir.length(); c.dir.normalize();
        const hit = raycastWalls(c.head, c.dir, len);
        if (hit) c.want.copy(c.head).addScaledVector(c.dir, Math.max(0.35, hit.distance - 0.18));
        // …and never past the city's edge: with the outer lanes walkable (WALK_EDGE),
        // a body at the kerb facing back into town would put the camera behind it in the void.
        if (cb) {
          c.want.x = Math.min(cb.max[0] - 0.2, Math.max(cb.min[0] + 0.2, c.want.x));
          c.want.z = Math.min(cb.max[1] - 0.2, Math.max(cb.min[1] + 0.2, c.want.z));
        }
        if (!c.init) { c.pos.copy(camera.position); c.init = true; }   // swoop out from wherever the camera was
        else c.pos.lerp(c.want, 1 - Math.exp(-16 * dt));
        camera.position.copy(c.pos);
      } else {
        camera.position.y = eye + lift;
        // The step's shoulder roll rides on top of the look direction.
        if (!gyroBridge.active && (roll !== 0 || !FEEL_CLASSIC)) {
          camera.quaternion.setFromEuler(look.current);
          if (roll) camera.rotateZ(roll);
        }
      }
    }
    // Broadcast my finalized position to peers in this area (throttled internally).
    // The BODY's position — in third person the camera is somewhere else.
    const me = thirdOn.current ? pvec.current : camera.position;
    emitMove([me.x, thirdOn.current ? eye : camera.position.y, me.z], camera.rotation.y);
    // AND TO THE DOM. WalkGuide's waypoint + Lyra's stall/stray nudges and the
    // map's "you are here" all read camBasis — which only InteriorWalk used to
    // write. On the street it sat at wherever you last stood indoors (or 0,0),
    // so the distance to the studio never changed: "stall" fired while you were
    // walking and "stray" could never fire at all.
    setCamPos(thirdOn.current ? { x: pvec.current.x, y: eye, z: pvec.current.z } : camera.position);
    setCamBasis(camera.getWorldDirection(fwdPub.current));
  });
  // Leaving the street rig (a room, the map, plan view) hands the camera back at
  // the body, not behind it.
  useEffect(() => () => {
    if (thirdOn.current) camera.position.set(pvec.current.x, eye, pvec.current.z);
    player.third = false;
  }, [camera, eye]);
  return null;
}

// InteriorWalk — first-person controls for a splat ROOM (firstPerson rooms).
// DRAG to look (the camera rotates in place — it does NOT float around a pivot
// the way OrbitControls does), WASD to pan around the room. Runs only after the
// entry fly-in (CameraRig) settles, adopting exactly where it landed. Position
// is kept in the room by CameraBounds. Arrows are intentionally NOT bound so
// they stay free for the ?cards=edit card editor.
// `speed` is the FALLBACK, not the rule — rooms.js `walkSpeed` wins, and
// ?walkspeed= beats both for tuning without a rebuild.
//
// ── WHY THIS WAS WRONG, AND INVISIBLY SO ─────────────────────────────────────
// The street walks at 9 u/s and this walked at 2.4 — the same body moving at
// 27% of its own pace the moment it steps through a door. Nobody caught it
// because no interior was big enough to walk in: the salon is 7 units across,
// so you reach the far wall before the speed means anything. The subway is 46.8
// units, the first room with real distance in it, and the difference reads as
// the room being sluggish rather than as a constant being wrong.
//
// Not simply raised to 9: a street is somewhere you cross and a room is
// somewhere you move around in, and 20 m/s indoors would be its own bug. This
// is a per-room decision, so the room makes it.
/**
 * ARROW KEYS TURN AN ORBIT ROOM'S VIEW (2026-11-02). Khari: "in the studio, I am
 * unable to use my arrows to pivot the camera. only the drag is working. it is a
 * splat like the record shop is. Please enable the keyboard movements."
 * A first-person room turns on the arrows in InteriorWalk; an ORBIT room (the
 * studio, the listening room, My Studio) is drei's OrbitControls, whose own
 * keyboard support PANS rather than turns, and was never switched on. Held
 * arrows turn it here instead — left/right swing round, up/down tilt, the same
 * way round as the record shop (left looks left, up looks up) — through the
 * controls' own angles, so the room's orbit limits (minPolar/maxPolar, minAz/
 * maxAz) still hold and the damping carries it. Stands down while typing, while
 * seated (SeatCamera has the camera), and under a tuning tool that uses arrows.
 */
function OrbitArrowKeys({ controls, enabled }) {
  const held = useRef({});
  useEffect(() => {
    if (!enabled) { held.current = {}; return undefined; }
    const KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']);
    // A placement tool's arrows move the thing being placed (plaques, the video
    // panel, billboards, routes): any open admin tool, or ?panel/cam=edit.
    const tuning = () => {
      if (useWorldStore.getState().adminTool) return true;
      try { return /[?&](panel|cam)=edit\b/.test(window.location.search); } catch { return false; }
    };
    const down = (e) => {
      if (!KEYS.has(e.code) || e.metaKey || e.ctrlKey || e.altKey || isTyping(e) || tuning()) return;
      held.current[e.code] = true;
      e.preventDefault();   // no page scroll under the canvas
    };
    const up = (e) => { if (KEYS.has(e.code)) held.current[e.code] = false; };
    const clear = () => { held.current = {}; };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', clear);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); window.removeEventListener('blur', clear); };
  }, [enabled]);
  useFrame((_, dt) => {
    const c = controls.current; const k = held.current;
    if (!enabled || !c || !c.enabled) return;
    const turn = ((k.ArrowLeft ? 1 : 0) - (k.ArrowRight ? 1 : 0)) * 1.6 * dt;   // rad/s, InteriorWalk's rate
    const tilt = ((k.ArrowUp ? 1 : 0) - (k.ArrowDown ? 1 : 0)) * 1.2 * dt;
    if (turn) c.setAzimuthalAngle(c.getAzimuthalAngle() + turn);
    if (tilt) c.setPolarAngle(c.getPolarAngle() + tilt);
  });
  return null;
}

function InteriorWalk({ speed: fallbackSpeed = 4.5, sensitivity = 0.0026 }) {
  const { camera, gl } = useThree();
  const keys = useRef({});
  const drag = useRef({ on: false, x: 0, y: 0 });
  const look = useRef(new THREE.Euler(0, 0, 0, 'YXZ'));
  const adopted = useRef(false);
  const eyeY = useRef(1.6);
  const camSettled = useWorldStore((s) => s.camSettled);
  const interior = useWorldStore((s) => s.interior);
  const seated = useWorldStore((s) => s.seated);
  const camVersion = useWorldStore((s) => s.camVersion);
  const freeCam = useWorldStore((s) => s.freeCam);
  // Reused every frame — getWorldDirection writes into whatever you hand it, and
  // allocating a Vector3 per frame in the walk loop is pure garbage churn.
  const fwdPub = useRef(new THREE.Vector3());
  const speed = qpNum('walkspeed') ?? ROOMS[interior]?.walkSpeed ?? fallbackSpeed;
  // THIRD PERSON IN A ROOM (2026-11-01, Khari: "3rd person POV does not work in
  // rooms. let's try to enable that"). With a body (a mannequin) and the view on
  // third (V / the HUD's view button), WASD moves the BODY (state/roomBody) and
  // RoomChase follows it with the camera; the floor, furniture and doors measure
  // from the body. Off while seated (the seat owns the camera), in freecam and in
  // VR. Leaving third hands the camera back at the body's eyes, exactly where a
  // first-person walk resumes.
  const hasBody = useHasBody() || TP_TEST_BODY != null;
  const leaveThird = () => {
    if (!roomBody.on) return;
    camera.position.copy(roomBody.v);
    roomBody.on = false; player.third = false; player.y = 0;
  };
  useEffect(() => () => leaveThird(), []); // eslint-disable-line react-hooks/exhaustive-deps
  // null until world.speed() turns it on — an always-on meter is a console line
  // every second forever, which trains you to ignore the console.
  const meter = useRef(null);
  const speedMeter = useWorldStore((s) => s.speedMeter);
  useEffect(() => {
    meter.current = speedMeter ? { dist: 0, ran: 0, t0: performance.now(), t: 0, wall: 0, frames: 0, px: camera.position.x, pz: camera.position.z } : null;
    if (speedMeter) floorBridge.reset();
  }, [speedMeter, camera]);

  // Pitch (tilt) limits — how far up/down the visitor can look. Authorable in
  // ?cam=edit (writes look:<room>) and bakeable to rooms.js `look`. The hard
  // ceiling stays just shy of ±90° so the view can't flip.
  const tilt = useRef({ min: -1.45, max: 1.45 });
  useEffect(() => {
    let lim = ROOMS[interior]?.look || {};
    try { const s = JSON.parse(localStorage.getItem(`look:${interior}`) || 'null'); if (s) lim = s; } catch (_) {}
    const clamp = (v, d) => Math.max(-1.5, Math.min(1.5, Number.isFinite(v) ? v : d));
    tilt.current = { min: clamp(lim.tiltMin, -1.45), max: clamp(lim.tiltMax, 1.45) };
    // Re-clamp the current look so a tightened limit takes effect immediately.
    look.current.x = Math.max(tilt.current.min, Math.min(tilt.current.max, look.current.x));
    if (!gyroBridge.active) camera.quaternion.setFromEuler(look.current);
  }, [interior, camVersion, camera]);

  useEffect(() => {
    const dom = gl.domElement;
    // Same one-pointer-id look-drag as street WalkControls (see there) so the
    // joystick finger and the look finger don't fight on touch.
    const onDown = (e) => { drag.current = { on: true, x: e.clientX, y: e.clientY, id: e.pointerId }; };
    const onMove = (e) => {
      if (!drag.current.on || drag.current.id !== e.pointerId || !adopted.current) return;
      const dx = e.clientX - drag.current.x, dy = e.clientY - drag.current.y;
      drag.current.x = e.clientX; drag.current.y = e.clientY;
      const L = look.current;
      L.y -= dx * sensitivity;
      L.x = Math.max(tilt.current.min, Math.min(tilt.current.max, L.x - dy * sensitivity));
      if (!gyroBridge.active) camera.quaternion.setFromEuler(L);
    };
    const onUp = (e) => { if (drag.current.id === e.pointerId) drag.current.on = false; };
    const down = (e) => {
      if (isTyping(e)) return;              // typing in a dock is not walking
      keys.current[e.code] = true;
      // F FOR FREE. A toggle rather than a held key: you fly up to look at
      // something and then want both hands back — holding a key while reading
      // coordinates off a panel is not a thing anyone can actually do.
      if (e.code === 'KeyF' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const st = useWorldStore.getState();
        const on = !st.freeCam;
        st.toggleFreeCam();
        // Re-adopt on the way back down, so the eye height comes from where you
        // are rather than snapping to where you took off.
        if (!on) adopted.current = false;
        console.log(`[cam] freecam ${on ? 'ON — Q rise / E fall, floor clamp stood down' : 'OFF — back on the floor'}`);
      }
    };
    const up = (e) => { keys.current[e.code] = false; };
    dom.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      dom.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera, gl]);

  useFrame((_, dt) => {
    // ── THE METER RUNS FIRST, BEFORE EVERY EARLY RETURN ──────────────────────
    //
    // It used to sit at the bottom, past `if (!camSettled) return` and
    // `if (seated) return`. A frame that took either of those applied no
    // movement AND never reached the meter — so dropped frames were subtracted
    // from the distance and from the clock alike, and the average came out at
    // the full cap no matter how many were lost. The instrument could not see
    // the one failure it was built to find.
    //
    // Time is counted whenever a movement KEY IS HELD, which is the honest
    // denominator: you asked to walk for a second, how far did you get.
    if (meter.current) {
      const m = meter.current;
      const kk = keys.current;
      const wants = !!(kk.KeyW || kk.KeyA || kk.KeyS || kk.KeyD) || moveBridge.x !== 0 || moveBridge.y !== 0;
      m.frames += 1;
      m.wall += dt;
      if (wants) {
        m.dist += Math.hypot(camera.position.x - m.px, camera.position.z - m.pz);
        m.t += dt;
      }
      m.px = camera.position.x; m.pz = camera.position.z;
      if (m.t >= 1) {
        // ── REAL TIME vs THE FRAME CLOCK ─────────────────────────────────────
        // Everything that moves you is multiplied by `dt`, so if `dt` is smaller
        // than the wall clock, the world runs in slow motion — smoothly, with no
        // stutter, and with a per-frame speed that measures as exactly correct.
        // Every previous version of this meter divided dt by dt and could only
        // ever report the cap back to itself.
        const realMs = performance.now() - m.t0;
        const drift = realMs / 1000 / m.wall;   // >1 means dt runs slow
        const probe = floorBridge.hit + floorBridge.miss;
        const pct = probe ? Math.round((floorBridge.hit / probe) * 100) : 100;
        const ups = m.dist / m.t;
        const realUps = m.dist / (realMs / 1000);
        console.log(`[speed] ${ups.toFixed(2)} u/s by frame clock  ·  ${realUps.toFixed(2)} u/s BY WALL CLOCK`
          + `  ·  at ${camera.position.x.toFixed(1)}, ${camera.position.z.toFixed(1)}  ·  cap ${speed}`
          + `  ·  walk ran on ${Math.round((m.ran / m.t) * 100)}% of frames  ·  probe ${pct}%`
          + `  ·  ${Math.round(m.frames / (realMs / 1000))} real fps`
          + (drift > 1.25 ? `  ← dt IS RUNNING ${drift.toFixed(1)}x SLOW: the world is in slow motion` : ''));
        m.dist = 0; m.t = 0; m.ran = 0; m.wall = 0; m.frames = 0; m.t0 = performance.now();
        floorBridge.reset();
      }
    }

    // A SCENE HAS THE CAMERA (a room's greeting, components/RoomGreeting): stand
    // down, body and all, and pick the view up again wherever it hands it back.
    if (cutsceneBridge.active) { adopted.current = false; return; }
    // A LOOK-ONLY room (rooms.js `lookOnly`, the record shop's splat): first person, never a body to chase.
    const lookOnly = !!ROOMS[interior]?.lookOnly && !freeCam;
    const thirdOK = camSettled && !seated && !freeCam && !lookOnly && hasBody && usePovStore.getState().wantThird
      && !gyroBridge.active && NEXT_LOOK && !FEEL_CLASSIC;
    if (!thirdOK && roomBody.on) leaveThird();
    if (!camSettled) { adopted.current = false; return; } // let CameraRig fly in
    // SITTING DOWN OR STANDING UP: SeatCamera is driving. Resetting `adopted`
    // means that when the move ends this picks the camera up wherever it landed
    // rather than snapping back to the height it remembered from standing.
    if (seated && seated.phase !== 'seated') { adopted.current = false; return; }
    if (!adopted.current) {
      adopted.current = true;
      // The height that walks: the body's in third person (the camera is behind it).
      eyeY.current = (roomBody.on ? roomBody.v : camera.position).y;
      look.current.setFromQuaternion(camera.quaternion); look.current.z = 0;
      look.current.x = Math.max(tilt.current.min, Math.min(tilt.current.max, look.current.x));
      if (!gyroBridge.active) camera.quaternion.setFromEuler(look.current);
    }
    // The body starts where your eyes are (the spawn, or where you stood up).
    if (thirdOK && !roomBody.on) { roomBody.v.copy(camera.position); roomBody.on = true; player.third = true; }
    const P = roomBody.on ? roomBody.v : camera.position;   // the point that walks
    // WASD pans; W/S only strafe in the horizontal plane so the eye stays level.
    const k = keys.current;
    // Arrow keys rotate the view here too (look without the trackpad).
    {
      const L = look.current;
      const rot = 1.6 * dt;
      let looked = false;
      if (k.ArrowLeft) { L.y += rot; looked = true; }
      if (k.ArrowRight) { L.y -= rot; looked = true; }
      if (k.ArrowUp) { L.x = Math.min(tilt.current.max, L.x + rot); looked = true; }
      if (k.ArrowDown) { L.x = Math.max(tilt.current.min, L.x - rot); looked = true; }
      if (looked && !gyroBridge.active) camera.quaternion.setFromEuler(L);
    }
    // SEATED: you may look around, you may not walk off in the chair. Returning
    // before the movement block (and before the eyeY pin, which would haul you
    // back to standing height) leaves SeatCamera holding the position.
    if (seated) { emitMove([camera.position.x, camera.position.y, camera.position.z], camera.rotation.y); return; }
    // LOOK ONLY: the same — turn where you stand, never walk (rooms.js `lookOnly`).
    if (lookOnly) { emitMove([camera.position.x, camera.position.y, camera.position.z], camera.rotation.y); return; }
    // ASKED vs KEPT. If these ever disagree, something between the walk code and
    // the end of the frame is taking movement away — and that difference is the
    // only thing that can make one PLACE slower than another, since `speed` is a
    // single constant for the whole room.
    let movedThisFrame = false;
    let asked = 0;
    const fwd = new THREE.Vector3(); camera.getWorldDirection(fwd); fwd.y = 0;
    if (fwd.lengthSq() > 1e-6) {
      fwd.normalize();
      const right = new THREE.Vector3().crossVectors(fwd, new THREE.Vector3(0, 1, 0)).normalize();
      const move = new THREE.Vector3();
      if (k.KeyW) move.add(fwd);
      if (k.KeyS) move.sub(fwd);
      if (k.KeyD) move.add(right);
      if (k.KeyA) move.sub(right);
      move.addScaledVector(fwd, moveBridge.y);      // on-screen joystick (touch)
      move.addScaledVector(right, moveBridge.x);
      if (move.lengthSq() > 0) {
        const run = (k.ShiftLeft || k.ShiftRight) ? 2 : 1;
        if (move.length() > 1) move.normalize();
        move.multiplyScalar(speed * run * boostFactor() * dt);   // × a GangstaFuel's minute (state/gymFuel)
        P.add(move);
        // Counted where movement is actually APPLIED. Against a denominator of
        // key-held time, this is the number that exposes skipped frames: 100%
        // means the walk code ran every frame you asked it to, anything less
        // means something above returned early and ate that frame whole.
        if (meter.current) meter.current.ran += dt;
      }
    }
    if (freeCam) {
      // Q RISE / E FALL. Vertical has to be its own input: WASD is strictly
      // horizontal by design (it keeps the eye level while you look around), so
      // simply unpinning y would leave you at whatever height you were already
      // at with no way to change it — which is exactly the trap this exists to
      // get out of.
      const vy = (k.KeyQ ? 1 : 0) - (k.KeyE ? 1 : 0);
      if (vy) camera.position.y += vy * speed * ((k.ShiftLeft || k.ShiftRight) ? 2 : 1) * dt;
      eyeY.current = camera.position.y;   // so dropping out of freecam stays put
    } else if (ROOMS[interior]?.multiLevel) {
      // A LOFT (2026-10-30, The Jahva House — the listening corner is upstairs).
      // Pinning y to the arrival eye is what kept every room one storey: each
      // frame put you back at the ground-floor eye before FloorClamp probed, so
      // its ray always started below the mezzanine and the stairs could lift
      // you 0.35u at most. Here the height is FloorClamp's alone — it runs after
      // this (mounted later) and stands you on whatever floor is under you, and
      // refuses the drop off the mezzanine edge. Tracked rather than pinned so
      // freecam and seating still pick up from where you actually are.
      eyeY.current = P.y;
    } else {
      P.y = eyeY.current;
    }
    // Published every frame so `world.where()` can answer without a probe
    // component being mounted. Authoring a spawn means standing where it should
    // be and reading the number off — the alternative is deriving it from model
    // bounds, which is what put this room's spawn inside a row of benches.
    //
    // BOTH, and that is the point. Publishing only the position left `where()`
    // computing its `look` from the module's DEFAULT heading (0, 0, −1), so
    // every spawn it printed faced −z regardless of which way you were actually
    // looking — a plausible-looking line that was quietly half made up. A
    // fabricated number is worse than a missing one, because it gets pasted.
    setCamPos(P);
    setCamBasis(camera.getWorldDirection(fwdPub.current));

    // ── THE SPEED METER (world.speed()) ──────────────────────────────────────
    // Measured AFTER every correction has run, so it reports the distance you
    // actually kept rather than the distance the movement code asked for. That
    // is the whole point: a rewind, a clamp or a bounds push all look identical
    // from inside the world — "it feels slow here" — and only differ in whether
    // the number moves.
    //
    // Paired with the probe's hit rate, because those are the two candidate
    // explanations and one line settles which. Constant speed with a 100% probe
    // means nothing is stealing movement and the room is lying to your eyes —
    // scale, or optical flow, not code.
    // ── MEASURED WHILE MOVING, NOT PER WALL-CLOCK SECOND ─────────────────────
    //
    // Averaging over a real second folds in every moment you were not pressing a
    // key, so a genuine slowdown and a half-second pause produce the same
    // reading — which is why the first version scattered (1.39 / 2.37 / 1.98)
    // instead of showing one number per place. Dividing distance by TIME SPENT
    // MOVING makes the value comparable between two spots, which is the only
    // question being asked.
    //
    // The position is stamped on every line for the same reason: "slower in the
    // train" needs the reading and the place in one row, or the comparison is
    // done from memory.
    if (meter.current) {
      const m = meter.current;
      const moved = Math.hypot(camera.position.x - m.px, camera.position.z - m.pz);
      m.px = camera.position.x; m.pz = camera.position.z;
      m.frames += 1;
      m.wall += dt;
      if (movedThisFrame) { m.dist += moved; m.asked += asked; m.t += dt; }
      // Report on moving time, so standing still never ends a window early and
      // never dilutes one. Nothing is printed while you are stationary.
      if (m.t >= 1) {
        const probe = floorBridge.hit + floorBridge.miss;
        const pct = probe ? Math.round((floorBridge.hit / probe) * 100) : 100;
        const ups = m.dist / m.t;
        const kept = m.asked > 0 ? Math.round((m.dist / m.asked) * 100) : 100;
        console.log(`[speed] ${ups.toFixed(2)} u/s while moving  ·  at ${camera.position.x.toFixed(1)}, ${camera.position.z.toFixed(1)}`
          + `  ·  cap ${speed}  ·  KEPT ${kept}% of what was asked  ·  probe ${pct}%  ·  ${Math.round(m.frames / m.wall)} fps`);
        m.dist = 0; m.asked = 0; m.t = 0; m.frames = 0; m.wall = 0;
        floorBridge.reset();
      }
    }
    emitMove([P.x, P.y, P.z], camera.rotation.y);
  });
  return null;
}

// Beat clock — derives the beat phase from the modulation track's BPM.
//
// ── PUBLISHED, NEVER SET AS STATE ───────────────────────────────────────────
// This used to `setPhase(p)` every frame, which made SceneContents — the parent
// of the whole city, every interior and the reflection <Environment> — re-render
// sixty times a second so that a handful of pulsing lights could read a prop.
// Measured (2026-09-23): React reconciliation of the scene tree on every frame,
// AND drei's <Environment frames={1}> re-running its cube-camera capture plus a
// full PMREM blur each time, because its children were new objects every render.
//
// The phase lives on beatBridge (mutated in place, read inside each consumer's
// own useFrame), which is what the residents' feet were already locked to.
// THE CAMERA BEHIND YOUR BODY IN A ROOM (2026-11-01). Mounted AFTER FloorClamp
// and SolidClamp, so it frames the body where they left it this frame. Close —
// rooms are a few metres across, not a road (the street's chase is 2.7 bodies
// back; this is 1.55) — over the right shoulder, and pulled in when the room is
// in the way, so a wall behind you never ends up between you and the camera.
// It also publishes the body (player.x/y/z, velocity) for PlayerBody.
const ROOM_CHASE = 1.55;
function RoomChase({ eye = 0.75 }) {
  const camera = useThree((s) => s.camera);
  const t = useMemo(() => ({
    fwd: new THREE.Vector3(), side: new THREE.Vector3(), head: new THREE.Vector3(),
    want: new THREE.Vector3(), dir: new THREE.Vector3(), prev: new THREE.Vector3(),
    ray: new THREE.Raycaster(), has: false, init: false,
  }), []);
  useFrame((_, dt) => {
    if (!roomBody.on || cutsceneBridge.active) { t.has = false; t.init = false; return; }
    const b = roomBody.v;
    const feet = b.y - eye;
    if (t.has && dt > 0) { player.vx = (b.x - t.prev.x) / dt; player.vz = (b.z - t.prev.z) / dt; }
    else { player.vx = 0; player.vz = 0; }
    t.prev.copy(b); t.has = true;
    player.x = b.x; player.z = b.z; player.y = feet; player.jump = 0;

    camera.getWorldDirection(t.fwd);
    t.side.set(-t.fwd.z, 0, t.fwd.x).normalize();
    const bh = player.height || 0.78;
    t.head.set(b.x, feet + bh * 0.92, b.z).addScaledVector(t.side, bh * 0.3);
    t.want.copy(t.head).addScaledVector(t.fwd, -bh * ROOM_CHASE);
    t.dir.copy(t.want).sub(t.head);
    const len = t.dir.length();
    const room = interiorFloor.object;
    if (room && len > 1e-3) {
      t.dir.multiplyScalar(1 / len);
      t.ray.set(t.head, t.dir); t.ray.far = len; t.ray.firstHitOnly = true;
      const hit = t.ray.intersectObject(room, true)[0];
      if (hit) {
        // PULLED IN BY THE ROOM (HQ's spawn stands half a unit from the glass):
        // the closer it has to come, the higher and wider it goes, so you look
        // over the shoulder rather than into the back of your own head.
        const d = Math.max(0.32, hit.distance - 0.12);
        const pull = 1 - d / len;
        t.want.copy(t.head).addScaledVector(t.dir, d)
          .addScaledVector(t.side, bh * 0.25 * pull);
        t.want.y += bh * 0.45 * pull;
      }
    }
    if (!t.init) { camera.position.copy(t.want); t.init = true; }
    else camera.position.lerp(t.want, 1 - Math.exp(-18 * dt));
  });
  return null;
}

function useBeat() {
  const modulation = useWorldStore((s) => s.modulation);
  const bpm = modulation?.bpm || 92;
  const energy = modulation?.energy ?? 0.5;

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    beatBridge.phase = ((t * bpm) / 60) % 1; beatBridge.bpm = bpm; beatBridge.energy = energy; beatBridge.t = t;
  });
  return { energy };
}

function SceneContents() {
  const zoom = useWorldStore((s) => s.zoom);
  const landmark = useWorldStore((s) => s.landmark);
  // Has the visitor clicked Enter? Used below to keep city life OFF the boot
  // critical path — see the note above <CityLife />.
  const entered = useWorldStore((s) => s.entered);
  const { energy } = useBeat();
  const inInterior = zoom === ZOOM.INTERIOR;
  // A mesh venue (rooms.js `model`) rather than a Gaussian-splat room — the two
  // want opposite things from the town's outdoor lighting rig.
  const interiorId = useWorldStore((s) => s.interior);
  const meshRoom = inInterior && !!ROOMS[interiorId]?.model;

  // HOW MUCH OF THE OUTSIDE A ROOM CAN SEE.
  //
  // Interiors are built at world ORIGIN, in the same coordinate space as the
  // city — they are not a separate scene, the street is simply not drawn while
  // you are inside. That is invisible until a venue has windows, and then it
  // matters: whatever is still mounted shows through them.
  //
  // The city itself cannot be one of those things. It occupies the origin too,
  // so drawing it would put buildings through the shop floor. But the DISTANT
  // layers sit hundreds of units out — the skyline ring, the sky shell — and a
  // 30-unit room fits inside them with room to spare. Those are exactly what a
  // window should show.
  //
  //   'none'      (default) sealed — what every splat room has always done
  //   'backdrop'  sky, skyline and fog: a city visible through glass, no street
  //
  // Street dressing — billboards, ad placements, traffic, residents — always
  // stays hidden, because it hangs on buildings that are not being drawn.
  //
  // A SITED room overrides all of this: it stands in the real city at its own
  // building's coordinates, so the street is simply left running. Nothing to
  // fake, and walking to a window shows the genuine block — traffic, billboards
  // and all — because it is the same scene you were standing in a moment ago.
  const sited = inInterior && ROOMS[interiorId]?.placement === 'sited';
  const exterior = inInterior ? (ROOMS[interiorId]?.exterior || 'none') : 'full';
  const showBackdrop = !sited && exterior === 'backdrop';

  // A ROOM THAT CAN SEE OUT KEEPS THE SUN.
  //
  // Withholding the town's rig is right for a SEALED room — it was lighting shop
  // interiors with a midday key aimed at street facades. It is wrong the moment a
  // window shows the real city: the street outside would be unlit while the room
  // was, which is the same mistake pointing the other way.
  //
  // So the outdoor rig follows the VIEW, not the room type, and MeshInterior's
  // own practicals supplement it rather than replace it. That is also what lets a
  // sited space share the world's clock — the light through its windows is the
  // same sun at the same hour as the street it opens onto, with no second system
  // to keep in step.
  const seesOutside = sited || showBackdrop;
  // Plan view drops the atmosphere entirely — see BuildView for why each of
  // these is in its way: fog hides the far half of the map, the day/night rig
  // decides how much of it is lit, and the sky layers are just expensive
  // scenery when the camera is pointed at the ground.
  const planView = useWorldStore((s) => s.planView);
  // POC: drop the generated avatar-3D resident into the street with ?resident=1
  // (placed beside the parked car where the fly-in lands).
  const RESIDENT = useMemo(() => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('resident') === '1', []);
  // City-life NPC system (Spec: two-tier). ?npc=1 populates the street with
  // featured-artist residents from the roster manifest (proximity/attention/engage).
  const NPC_MODE = useMemo(() => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('npc') === '1', []);
  const residentPos = landmark ? [landmark[0] + 2.4, 0, landmark[2] + 1.5] : [6, 0, 6];
  // The town lives on Oakland local time (district config); art-directed
  // keyframes turn the hour into light. ?time=golden|night|… pins it (demos).
  // A room may pin the hour (rooms.js `timeOfDay`: 'dusk' | 'night' | 18.5) so a
  // written scene happens at the hour it was written for. Unset = the live clock.
  const tod = useWorldTime(SLICE_DISTRICT.timezone, inInterior ? (ROOMS[interiorId]?.timeOfDay ?? null) : null);
  // THE NEW LOOK IS A STREET LOOK. Outside, the procedural sky, the sun rig, the
  // sky reflections and the screen-space haze take over. Inside any room —
  // sited, backdrop or sealed — everything stays exactly as it was tuned: the
  // classic light numbers, the lightformer reflections, the panorama sky
  // through the windows. Those values were won by standing in each room
  // (feedback_world_per_room_values) and are not this pass's to move.
  const nextStreet = NEXT_LOOK && !inInterior && !planView;
  // The haze lives in the post pass, which split-screen VR displaces — so the
  // classic fog comes back whenever the pass is not the one drawing.
  const stereo = useWorldStore((s) => s.stereo);
  const postHaze = nextStreet && QUALITY.post && QUALITY.hazeOn && !stereo;
  const roomPin = inInterior ? (ROOMS[interiorId]?.timeOfDay ?? null) : null;

  return (
    <>
      {NEXT_LOOK && <SkyClock timeZone={SLICE_DISTRICT.timezone} pin={roomPin} />}
      {/* Backstop behind everything — outside the Sky dome (and in rooms) the
          clear color must reflect the town's hour, never default grey. */}
      {/* Plan view gets a flat neutral ground to read against: the sky layers are
          off, so without this the map would sit on whatever the last clear
          colour was. */}
      <color attach="background" args={[planView ? '#0e1014' : (inInterior ? '#070709' : tod.sky)]} />
      {/* A GLB VENUE BRINGS ITS OWN LIGHT. The town's rig is an outdoor one —
          midday ambient plus a hard key aimed at the road-facing facades — and
          it was applying inside the interiors too, because only the sky, fog and
          environment were ever gated on `inInterior`. The splat rooms did not
          care (their lighting is baked into the splat), so nothing exposed it
          until a PBR mesh room walked in and got whitewashed by a sun shining
          through its walls. Those rooms light themselves — see MeshInterior. */}
      {!NEXT_LOOK && !planView && (!meshRoom || seesOutside) && <ambientLight intensity={tod.ambIntensity} color={tod.ambColor} />}
      {/* Key light on the −z side so it lands square on the road-facing facades */}
      {!NEXT_LOOK && !planView && (!meshRoom || seesOutside) && (
        <directionalLight
          position={tod.keyPos}
          intensity={tod.dirIntensity}
          color={tod.sunColor}
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />
      )}
      {/* The same mounting rule, one rig: the sun (and moon) on the street, the
          classic numbers in any room that keeps the outdoor light. */}
      {NEXT_LOOK && !planView && (!meshRoom || seesOutside) && (
        <WorldLight tod={tod} mode={nextStreet ? 'sun' : 'classic'} />
      )}
      {/* ── SCENE-LEVEL SHADER STATE — NEVER UNMOUNTED, IN ANY VIEW ──────────
          `USE_FOG` and `USE_ENVMAP` are shader DEFINES. Setting scene.fog or
          scene.environment object→null does not turn an effect off, it changes
          what every material in the scene compiles to — so three recompiles
          thousands of programs, synchronously, on the main thread.

          This was already understood for the plan-view toggle (fog is pushed out
          of range there rather than removed). The interior boundary kept doing
          the thing the comment warned against, twice over:

            <Environment> was gated on `!inInterior`, so it unmounted for EVERY
            interior; and the whole block was gated on `(!inInterior ||
            showBackdrop)` — where showBackdrop is `!sited && ...`, so a SITED
            room like the corner market failed both halves and dropped the fog
            as well.

          Sited rooms are the worst possible case for it, because the city stays
          fully rendered around them: entering and leaving one recompiled every
          material in the city, twice per visit. That is the multi-second stall
          on desktop, and it is what crashed the phone on the way out.

          The cost of keeping them mounted is that interiors now receive the
          city's environment reflections. For a sited room that is correct — you
          can see the street through the window. If a room ever looks too
          reflective, the answer is that room's `light` multiplier, never
          unmounting this. */}
      {/* On the new-look street the haze is drawn by the post pass (height fog
          with sun scatter, from the depth buffer), so three's fog is pushed out
          of range — the plan-view trick: the define stays, the effect goes. */}
      <fog attach="fog" args={planView ? [tod.fog, 1e7, 2e7] : (postHaze ? [tod.fog, 1e6, 2e6] : [tod.fog, 80, 260])} />
      {/* Procedural reflection environment — no external HDRI. Emissive
          lightformers stand in for the sky and street practicals so chrome,
          storefront glass and the wet alley floor mirror the city (the
          webgl_materials_car look, self-contained). Rendered once. */}
      {NEXT_LOOK ? (
        // Same capture in rooms; the real sky on the street. See EnvProbe.
        <EnvProbe tod={tod} mode={nextStreet ? 'sky' : 'classic'} />
      ) : (
        <Environment resolution={256} frames={1}>
          <color attach="background" args={['#05060a']} />
          <Lightformer intensity={0.6 + tod.night * 0.5} color={tod.sky} form="rect" scale={[60, 30, 1]} position={[0, 20, -40]} rotation={[0, 0, 0]} />
          <Lightformer intensity={1.2 + tod.night * 1.6} color="#ffb25e" form="rect" scale={[30, 1.2, 1]} position={[0, 5, -12]} rotation={[Math.PI / 2, 0, 0]} />
          <Lightformer intensity={0.9 + tod.night * 1.4} color="#ff5e78" form="rect" scale={[16, 1, 1]} position={[-16, 6, 8]} rotation={[0, Math.PI / 2, 0]} />
          <Lightformer intensity={0.9 + tod.night * 1.4} color="#7dd3ff" form="rect" scale={[16, 1, 1]} position={[16, 7, 8]} rotation={[0, -Math.PI / 2, 0]} />
        </Environment>
      )}
      {/* The sky, by contrast, IS geometry you can see — a textured dome, not a
          define — so it stays gated. Nothing recompiles when it leaves. */}
      {(!inInterior || showBackdrop) && !planView && (nextStreet ? <ProceduralSky /> : <SkyDome />)}
      {/* Weather you can see (render/weather + Rain): the street only. */}
      {nextStreet && <Rain />}
      {nextStreet && <SkyTraffic />}

      {/* THE CITY — on the street always, and while inside a SITED room, since
          that room stands in it. Culled hard indoors (CityModel: SITED_CULL). */}
      {(!inInterior || sited) && (
        MODEL_MODE
          ? <Suspense fallback={null}><CityModel night={tod.night} /><VenueMarkers />
              {/* Billboards and the welcome sign are things you READ from the
                  street; from overhead they are edge-on rectangles that still
                  cost their textures and their video slots. */}
              {!planView && <><CityBillboards /><WelcomeSign /></>}
              {/* The backdrop layers are scenery for a camera at street level. From
                directly overhead they are off-frame at any useful zoom, and the
                shell's sky sphere would sit between the plan camera and the map. */}
              {!planView && <><CitySkyline ground tint={!nextStreet} /><CityBox groundColor={tod.fog} proceduralSky={nextStreet} /></>}
              {/* Measuring tape for siting work — the building's footprint and
                  the interior's, on the same map at the same scale. */}
              {planView && <VenueFootprints />}
              {/* A sited venue's OUTSIDE. Without this, moving one off an
                  existing building leaves an invisible hole you walk into. */}
              {/* The street lamps — read off the model's own road layout, one
                  light texture for all their pools. New look only. */}
              {NEXT_LOOK && !planView && <StreetLights />}
              <VenueShells />
              {/* Walk-in doors. Placed instances of a registered asset, not a
                  venue's exterior — so a city can have as many entrances as it
                  has corners, all sharing one asset_id. */}
              <Portals />
              {/* The venues the town voted for: a door, a lamp and their name
                  on the building their site names (dark when they are shut). */}
              <VenueFacades />
              {/* A venue raised on an empty lot gets its building (HQ, behind the spawn). */}
              <LotBuildings />
              {/* ⚙ → Sites: runs the survey and draws its ghosts (tool open only). */}
              <SiteSurvey />
              {/* Cuts the station's hole through the CITY MODEL's ground, which
                  the CityBox disc subtraction cannot reach — that ground is the
                  block pads (the inverse of the streets) baked into the GLB. */}
              <GroundPits />
              {/* The footprint brush — draws the armed venue's real outline
                  under the cursor so "will it fit" is answered before the
                  click. Not gated on planView: it works from the street too,
                  it is just far easier to judge from above. */}
              <VenuePlacer />
              {/* THE ONE LOADER for the streamed street kinds (buildings,
                  residents). Mounted unconditionally: it decides internally
                  whether to stream by cell or load the whole street, because
                  plan view needs everything at once and the street needs only
                  what is near you. Consumers below just read the store. */}
              <StreetStream />
              <BlockGrid /><PlacedBuildings />
              {/* CITY LIFE STANDS DOWN IN BUILD VIEW.
                  Traffic, residents and the guard are the most expensive things
                  in the scene — animated skinned meshes, per-frame route and
                  proximity work, one draw each — and every one of them is
                  irrelevant to laying out a map. Worse, the plan camera frames
                  the WHOLE city, so nothing is off-screen and nothing is culled:
                  every car and every NPC in town is drawn and stepped at once,
                  which is exactly the load the street view never has to carry.
                  The map itself is what build view is for. */}
              {/* TRAFFIC IS THE EXPENSIVE HALF; PEOPLE ARE THE HALF WORTH
                  KEEPING.
                  Cars are off indoors and in build view: each is a real vehicle
                  model on a per-frame curve, they are the bulk of city life's
                  cost, and from inside a venue SITED_CULL has already hidden the
                  street they drive on — so most of them are being animated
                  behind a wall. Losing them through the shop window is a real
                  cost, and cheaper than the frames they take.
                  Residents stay. A street with people on it is what makes the
                  city read as inhabited rather than as a diorama, and that is
                  worth seeing from a window; the guard is one model and belongs
                  with them. Build view still drops both — nothing is culled when
                  the camera frames the whole map. */}
              {/* ── LOADED BEHIND THE GATE, NOT DURING THE FLY-IN ───────────
                  (Khari, 2026-08-02, reversing the previous trade.) These used
                  to defer until Enter so the boot stayed light — but that
                  moved their decode/upload INTO the fly-in, which is the one
                  animation that must not stutter, and left the city visibly
                  populating after you land. The numbers changed too: the trade
                  was priced against ~7MB of traffic models, and the fleet is
                  one 0.28MB Elise now.
                  So they mount with the page. Every load lands while the
                  loading overlay hides the jank, THREE's loading manager
                  counts them, and the Loader's quiet-window holds the Enter
                  button until the chain is genuinely done — the gate carries
                  the weight, and the fly-in plays over a finished city. */}
              {!planView && !inInterior && <CityLife />}
              {/* Cars you can take (F) — parked at the kerb, read off the road map. */}
              {!planView && !inInterior && !FEEL_CLASSIC && <Vehicles eye={EYE} />}
              {/* Your own body, when the camera is behind it (third person). */}
              {!planView && !inInterior && !FEEL_CLASSIC && NEXT_LOOK && <PlayerBody />}
              {!planView && <><Bouncers />{NPC_MODE && <NpcManager />}</>}
              {/* THE LIVING CITY (city/, 2026-10-23): the artists live here, in
                  their own bodies, on their own days — and the places they live
                  their days in. NpcManager still loads the bodies; once the
                  city is live it stops standing them on fixed posts. */}
              {!inInterior && <CityPlaces />}
              {/* HypeTown Heli Tours (city/heliTour): the top-left lot's four pads and their helicopters. */}
              {!inInterior && <Suspense fallback={null}><Heliport /></Suspense>}
              {!planView && !inInterior && NPC_MODE && <CityResidents />}
              {!planView && !inInterior && <CityTalk />}
              {!planView && !inInterior && <CityStage />}
              {/* The street's music, each source a person's: the record store, a couple of car radios. */}
              {!planView && !inInterior && <CitySounds />}
              {/* Stepping into a scene: it takes the camera, then gives it back
                  (city/cutscene). WalkControls stands down while it holds it. */}
              {!planView && !inInterior && !MOMENT_ID && <CityCutscene />}
              {/* An episode's scene goes INSIDE: the view switches into the room and
                  back out after (no fly-in). Not gated on the street: it survives the switch. */}
              {!planView && <StoryRoomSwitch />}
              {!planView && RESIDENT && (
              <AnimatedResident
                // Hardcoded because this POC predates the roster — which is why
                // it sat on the pre-rebuild v1 model at the default 1.8 long
                // after every resident had been rebuilt and re-tuned. roster.json
                // is canonical; ?npc=1 is the path that reads it.
                meshUrl="/models/avatars/rebel_v2_rigged.glb"
                clipUrls={['/models/avatars/rebel_v2_walk.glb', '/models/avatars/rebel_v2_idle.glb']}
                height={0.75}
                play="walk"
                beatSync
                gaitOctave={4}
                footPhase={0.33}
                gaitBpm={118}
                position={residentPos}
                rotationY={Math.PI}
                talkAware
                onSelect={() => useWorldStore.getState().openChat({ avatarId: 26, name: 'Rebel Rhythm' })}
              />
            )}</Suspense>
          : OSM_MODE
            ? <OsmCity night={tod.night} />
            : <Street energy={energy} night={tod.night} />
      )}

      {/* THE INTERIOR — mounted whenever you are inside one, INDEPENDENTLY of
          whether the city above is also drawn.
          These two used to be the arms of a ternary, which was right while every
          room was virtual: you were either on the street or in a room, never
          both. A SITED room is both — it stands in the real city and its windows
          look out on it — and adding `sited` to the city side of that ternary
          therefore did more than keep the city: it took the ROOM away. Entering
          the shop mounted no <Interior>, so no MeshInterior, so no shop to stand
          in and no footprint to measure. Two conditions, not two branches. */}
      {inInterior && (
        <>
          {/* The far layers only — hundreds of units out, so a room sits well
              inside them. No city, no street dressing. A sited room skips this:
              it has the actual city instead. */}
          {showBackdrop && (
            <Suspense fallback={null}>
              <CitySkyline ground />
              <CityBox groundColor={tod.fog} />
            </Suspense>
          )}
          <Interior energy={energy} />
          {/* Your body indoors, in third person (state/roomBody). */}
          {!planView && !FEEL_CLASSIC && NEXT_LOOK && <PlayerBody />}
        </>
      )}
      {/* A chat as a scene (components/SceneChat): the camera to whoever you are talking to, anywhere. */}
      {!MOMENT_ID && <ChatSceneCamera />}
      {/* Hype-U's elevator: the camera behind you in the car, through the ride (components/hypeu). */}
      {!MOMENT_ID && <ElevatorDirector />}
      {!MOMENT_ID && <HeliDirector />}
      {/* A MOMENT'S STILL (city/moment, render-bot): LAST, so its camera is the frame's final word. */}
      {MOMENT_ID && <MomentDirector />}
    </>
  );
}

// THE NEAR PLANE FOLLOWS THE SCALE OF WHERE YOU ARE (2026-10-30: sitting at a
// table in The Jahva House, the front half of the table — and the coffee on it —
// was cut out of view, and came back when you stood and stepped away). The
// Canvas starts at near 0.3, chosen for the street and the fly-overs; indoors a
// person is 0.75 units tall, so 0.3 is about 66 cm, and anything that close (a
// table you sit at, a counter you lean on) was clipped. Indoors it is 0.05
// (~11 cm); out on the street it goes back to 0.3, so the city keeps its depth
// precision at a distance.
//
// AND CLOSER STILL WHEN SOMEONE IS THAT CLOSE (2026-11-02, Khari: "when chatting
// with artists, sometimes the camera tears into the model … I don't want to pull
// the camera back, I would prefer we allow the model to be this close without
// tearing"). The chat shot (ChatSceneCamera) stands half a unit off the face and
// then holds still; an artist who turns or steps toward you comes inside the
// near plane, and the front of the head is cut away. While a chat scene holds
// the camera the plane comes in to ~2 cm indoors (a room is small: no depth
// precision to lose) and ~11 cm on the street (the city still stretches 1,200
// units behind them). The helicopter's window seat sits inches from the seats
// and the pilot beside it, so it gets the indoor plane too.
const NEAR_STREET = 0.3;
const NEAR_INDOORS = 0.05;
const NEAR_CLOSE_INDOORS = 0.01;
const NEAR_CLOSE_STREET = 0.05;
function NearPlane() {
  const camera = useThree((st) => st.camera);
  const zoom = useWorldStore((st) => st.zoom);
  const chatting = useSceneChat((st) => !!st.on);
  const inCabin = useHeliTour((st) => !!st.mine && st.view === 'cabin');
  useEffect(() => {
    const indoors = zoom === ZOOM.INTERIOR;
    const close = chatting || inCabin;
    const near = indoors ? (close ? NEAR_CLOSE_INDOORS : NEAR_INDOORS) : (close ? NEAR_CLOSE_STREET : NEAR_STREET);
    const apply = () => { if (camera.near !== near) { camera.near = near; camera.updateProjectionMatrix(); } };
    // Out of a close-up the plane goes back only once the camera has eased away
    // (ChatSceneCamera OUT_S 0.9 s) — sooner, and the last second tears.
    if (!close && near > camera.near) { const t = setTimeout(apply, 1200); return () => clearTimeout(t); }
    apply();
    return undefined;
  }, [camera, zoom, chatting, inCabin]);
  return null;
}

export default function PlanetScene() {
  const controls = useRef();
  const zoom = useWorldStore((s) => s.zoom);
  const interior = useWorldStore((s) => s.interior);
  const camEdit = useWorldStore((s) => s.adminTool) === 'cam'; // camera tuning (AdminPanel)
  const camVersion = useWorldStore((s) => s.camVersion);
  const camSettled = useWorldStore((s) => s.camSettled);
  // Read here, not just inside InteriorWalk: an ORBIT room needs it too, so
  // OrbitControls can stand down while SeatCamera owns the camera. See its
  // `enabled` prop below.
  const seated = useWorldStore((s) => s.seated);
  const inInterior = zoom === ZOOM.INTERIOR;
  // Street-level walking for the city model (first-person). The orbit→street
  // fly-in runs FIRST (via CameraRig); walking engages only once it settles at
  // eye level, taking over from exactly where the descent landed.
  const walking = MODEL_MODE && !inInterior && zoom === ZOOM.STREET && camSettled;
  // DRONE. The building tool is used from above, and from above the camera is
  // OrbitControls — which swings around a pivot and has no WASD at all, so you
  // cannot fly to the block you want to edit. Opening the tool therefore swaps
  // in the walk rig with its leashes off: WASD to fly, Q/E for height, drag to
  // look, no eye-height lock and no map boundary.
  //
  // INSIDE A ROOM TOO, for the camera tool. Measuring a spawn or pacing out walk
  // bounds means getting to the corners and to head height, and an interior pins
  // the camera to eye level every frame and clamps it to whatever bounds already
  // exist — so the one job the tool is for is the one thing the controls prevent.
  // `blocks` stays outdoors, where it is used; `cam` is allowed both.
  const adminTool = useWorldStore((s) => s.adminTool);
  const drone = ((adminTool === 'blocks' || FREECAM) && !inInterior) || adminTool === 'cam';
  // Plan view owns the camera outright — it swaps in an orthographic one, so it
  // has to displace the walk rig, the drone and orbit alike, not sit alongside
  // them. Two components with `makeDefault` would fight over which camera the
  // scene is actually using.
  const planView = useWorldStore((s) => s.planView);
  // Mounted conditionally, because subscribing at priority 1000 is what takes
  // rendering over — see StereoRender.
  const stereoOn = useWorldStore((s) => s.stereo);
  const renderDpr = useWorldStore((s) => s.renderDpr);
  // In a car the keycaps go away (the drive HUD has its own hint).
  const driving = useDriveStore((s) => s.driving) != null;
  // …and during a cutscene: the scene has the camera, the keys would do nothing.
  const inCut = useCutscene((s) => s.phase === 'cut');
  // A camera config tuned in ?cam=edit persists per room (localStorage) and
  // wins over the rooms.js default — same contract as panel placement.
  const orbit = useMemo(() => {
    if (inInterior && interior) {
      try {
        const saved = JSON.parse(localStorage.getItem(`orbit:${interior}`) || 'null');
        if (saved) return saved;
      } catch (_) {}
    }
    return ROOMS[interior]?.orbit || { min: 0.5, max: 6 };
  }, [interior, inInterior, camVersion]);

  // firstPerson rooms use stand-still drag-look + WASD instead of orbit.
  const interiorFirstPerson = inInterior && !!ROOMS[interior]?.firstPerson;

  // near 0.3 (not 0.1): depth precision scales with the near plane — at
  // 0.1 the far ground layers z-fight. Interior min orbit is 0.5, safe.
  return (
    <>
    <Canvas
      shadows={!IS_MOBILE}
      camera={{ position: [0, 140, -160], fov: 55, near: 0.3, far: 1200 }}
      // `stencil` is FALSE by default in three 0.180 — the buffer is not even
      // allocated unless asked for, and every stencil operation then silently
      // does nothing. GroundPits cuts the stations' holes with it, so without
      // this the ground simply stays put with no error anywhere.
      gl={{ antialias: !IS_MOBILE, powerPreference: 'high-performance', stencil: true }}
      // Starts at the display's ratio (capped at 2; phones fixed at 1) and
      // adapts to the frame rate from there — see AdaptiveResolution.
      dpr={renderDpr ?? START_DPR}
    >
      {/* ⚠ A BOUNDARY INSIDE THE CANVAS, AROUND EVERYTHING (2026-10-31). r3f v8's
          Canvas has its own top-level Suspense whose fallback THROWS A
          NEVER-RESOLVING PROMISE INTO THE PAGE'S TREE ("Block") for as long as
          anything in the scene is suspended there. With no DOM boundary above
          the Canvas, that suspends the WHOLE APP ROOT: every ordinary DOM update
          stalls behind the 3D load — the door's percentage (Khari: "it loads
          quickly to 99 and freezes on 99 without visual update until it is
          done"), its switch to Enter, the HUD. Caught here instead, a suspended
          model only holds the scene, which shows nothing until it resolves
          exactly as r3f's own fallback did, and the page stays live. */}
      <Suspense fallback={null}>
      <AdaptiveResolution />
      {/* THE NEW LOOK'S CAMERA SIDE — ambient occlusion, haze, bloom, grade.
          Never alongside StereoRender (both take the render loop) and not in
          plan view (an orthographic map has no atmosphere to draw). Indoors it
          stays mounted but steps aside: rooms render exactly as they were tuned. */}
      {NEXT_LOOK && QUALITY.post && !planView && !stereoOn && <PostFX bypass={inInterior} />}
      {planView ? (
        <BuildView />
      ) : walking || drone ? (
        <>
          <WalkControls eye={EYE} drone={drone} />
          {/* Spor1's ride: while worldStore.ride is set this owns the camera
              and WalkControls stands down — one claimant at a time, always. */}
          <RideSequence eye={EYE} />
        </>
      ) : (
        <>
          {/* firstPerson rooms: stand-still drag-look + WASD (no orbit float).
              CameraRig still flies the entry, then InteriorWalk adopts it. */}
          {interiorFirstPerson ? (
            <InteriorWalk />
          ) : (
            <OrbitControls
              ref={controls}
              makeDefault
              // ── SITTING DOWN TAKES THE CAMERA AWAY FROM ORBIT ──────────────
              // Khari, 2026-08-07: "we still have an issue with the way the
              // camera is directed in the listening room."
              //
              // OrbitControls re-aims the camera AT ITS TARGET every frame. So
              // in an orbit room, SeatCamera would turn you to the seat's facing
              // and orbit would immediately turn you back to face the pivot —
              // which sits in the middle of the room, i.e. roughly at the
              // furniture. The seat's yaw was not being misread; it was being
              // overwritten, which is why flipping the seat changed nothing.
              //
              // The listening room is an ORBIT room (rooms.js gives it `orbit`,
              // not `firstPerson`), so the first-person controller's existing
              // "SeatCamera is driving, stand down" rule never applied here.
              // Same rule, stated for orbit: one claimant at a time.
              enabled={!seated}
              // iOS: pinch-to-zoom (dolly) on the canvas crashes Safari — disable it.
              enableZoom={!IS_IOS}
              // Pan lets you slide the pivot to look anywhere in a room (incl.
              // behind you). On per-room for the roomy interiors; off elsewhere.
              // Interiors pan per-room; the district/orbit OVERVIEW pans freely so
              // you can move across the city to edit positions + lanes.
              enablePan={inInterior ? !!orbit.pan : true}
              screenSpacePanning
              enableDamping
              rotateSpeed={orbit.rotateSpeed ?? 1}
              zoomSpeed={orbit.zoomSpeed ?? 1}
              dampingFactor={orbit.damping ?? 0.08}
              // Interior constraints engage only AFTER the fly-in settles —
              // clamping the camera mid-flight derails the entry framing.
              minDistance={inInterior ? (camSettled ? orbit.min : 0.01) : 4}
              maxDistance={inInterior ? (camSettled ? orbit.max : 2000) : 220}
              minPolarAngle={inInterior && camSettled ? (orbit.minPolar ?? 0.25) : 0}
              maxPolarAngle={inInterior ? (camSettled ? (orbit.maxPolar ?? Math.PI / 1.9) : Math.PI) : Math.PI / 2.05}
              minAzimuthAngle={inInterior && camSettled && orbit.minAz != null ? orbit.minAz : -Infinity}
              maxAzimuthAngle={inInterior && camSettled && orbit.maxAz != null ? orbit.maxAz : Infinity}
            />
          )}
          <CameraRig controls={controls} />
          <OrbitArrowKeys controls={controls} enabled={inInterior && !interiorFirstPerson && !seated && camSettled && !planView} />
          <AutoEnter />
        </>
      )}
      {/* Both of these move the camera. CameraBounds clamps it inside a room's
          measured extents and CameraBridge applies a tuned pose — either one
          would drag the plan camera off the overhead axis it exists to hold. */}
      {!planView && <CameraBounds />}
      {/* Bounds derived from the floor itself — walkable is wherever there is
          something to stand on. Also does the ground-following that makes
          stairs walkable. */}
      {/* Furniture is in the way (state/interiorSolids): pushed out of it
          before the floor is probed, so the floor is read where you end up. */}
      {!planView && interiorFirstPerson && <SolidClamp eye={EYE} />}
      {!planView && <FloorClamp eye={EYE} />}
      {/* Third person in a room: the camera follows the body the clamps just placed. */}
      {!planView && interiorFirstPerson && <RoomChase eye={EYE} />}
      {/* The phone's orientation owns the camera's ROTATION while VR is on;
          locomotion keeps the position. Mounted after FloorClamp so the two
          never argue about which one moves you — they touch different things. */}
      <GyroLook />
      {/* ?facing=1: an arrow on every body, bench and prop showing which way
          the code thinks it faces (components/FacingOverlay). */}
      {FACING_OVERLAY && <FacingOverlay />}
      {/* A GangstaFuel's minute: the world's canvas brightens (EnergyBoost). */}
      <BoostGlow />
      {stereoOn && <StereoRender />}
      {!planView && <CameraBridge />}
      <SceneContents />
      {/* Shader programs are built behind the landing page, not mid fly-in. */}
      <WarmShaders />
      {/* No receivers in view → no per-frame shadow pass. */}
      <ShadowGovernor />
      {/* Other people in your current area (multiplayer presence) */}
      <Peers />
      {/* The ending (§4.7): after the handoff call, she walks away into the
          city. Touches nothing but its own group — the camera stays yours. */}
      <HandoffWalkaway />
      {/* The beginning: Spor1's yellow Lotus, parked on the main strip while
          the ride quest is open. Lyra's call sends you to find it. */}
      <Spor1Lotus />
      {/* Ad/promo display surfaces (+ the admin placement ghost).
          INDOORS TOO, NOW THAT PLACEMENTS KNOW WHICH ROOM THEY ARE IN.
          This used to be street-only, and the reason was sound: a virtual room is
          built at world ORIGIN in the city's own coordinate space, so every street
          placement kept rendering around you while you were inside — hanging in
          mid-air on buildings that were not being drawn. The gate was a blunt fix
          for placements having no scope.
          They have one now (`zone` = the room id, see PlacementLayer), so the
          layer can be mounted everywhere and each surface shows up in exactly one
          place. Without this, a room could never carry an ad at all. */}
      {/* Still off in build view: these are wall-mounted quads, so from directly
          overhead they are edge-on lines that still pay for their house-fill
          textures and video slots. */}
      {!planView && <PlacementLayer />}
      {/* FIRST PERSON: the phone as an object in the player's hand. Third
          person keeps it in the HUD (<Phone/>); this self-hides unless the POV
          is first AND they have earned a body to hold it with. */}
      {/* The camera as the player's body while first person has no body yet. */}
      <SeatCamera />
      <NearPlane />
      <PhoneInHand />
      </Suspense>
    </Canvas>
    {/* Plain DOM — must live OUTSIDE the Canvas (r3f can't render DOM nodes) */}
    {camEdit && <CameraPanel />}
    {new URLSearchParams(window.location.search).get('beat') === '1' && <BeatHud />}
    {/* Locomotion UI in walkable modes: touch gets the joystick, larger/desktop
        views get an on-screen WASD cluster (drag-to-look works on both). */}
    {/* THE JOYSTICK SURVIVES, PER EYE. Losing it in split view would leave you
        able to look and unable to move, which is the one combination that has no
        way out. Duplicated rather than hidden because it is a touch target: both
        copies write the same moveBridge, so either thumb works.
        Honest caveat: in an actual holder the screen is unreachable and this is
        useless — that case wants gaze or auto-walk, which is a separate feature.
        Handheld split view, which is how this gets TESTED, needs it. */}
    {/* A cutscene has the camera and the controls stand down — no joystick on
        screen to suggest otherwise (it showed through the letterbox on phones). */}
    {(walking || (interiorFirstPerson && !ROOMS[interior]?.lookOnly)) && !inCut && (IS_TOUCH
      ? <VRSafe><TouchControls /></VRSafe>
      : !driving && <VRHide><WasdControls /></VRHide>)}
    </>
  );
}